import {
  CalendarDaysIcon,
  CheckIcon,
  ChevronDownIcon,
  ClipboardListIcon,
  CommandIcon,
  DollarSignIcon,
  FileTextIcon,
  FileUpIcon,
  HammerIcon,
  HomeIcon,
  KeyRoundIcon,
  LayoutDashboardIcon,
  LoaderCircleIcon,
  LogInIcon,
  ListIcon,
  MoreHorizontalIcon,
  PaperclipIcon,
  PanelLeftIcon,
  PanelRightIcon,
  PlusIcon,
  RotateCcwIcon,
  SearchIcon,
  StickyNoteIcon,
  TrashIcon,
  UsersIcon,
  WrenchIcon,
  XIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ComponentType } from "react";
import { Link, Navigate, NavLink, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";

import { createCommandControlClient, setCommandControlAuthToken } from "@command-control/client-runtime";
import type { AreaSystemRecord, AttachmentRecord, HouseholdRecord, ProfessionalRecord, ProjectRecord } from "@command-control/contracts";

import {
  authHandoffUrl,
  clearStoredAuthToken,
  getAuthSession,
  parseAuthHandoffUrl,
  signOutOfAuth,
  storeAuthToken,
  storedAuthToken,
  type AuthSession,
} from "./auth";
import {
  getNeedsAttention,
  getScheduleItems,
  rollupProjectCosts,
  type Invoice,
  type DatedHouseholdItem,
  type MaintenanceItem,
  type MaintenanceFrequency,
  type Permit,
  type ProfessionalInteraction,
  type ProjectQuote,
  type ProjectStatus,
  type ProjectSupply,
  type ProjectTodo,
} from "../../../packages/shared/src/household-domain.ts";

const commandControlClient = createCommandControlClient(
  import.meta.env.VITE_COMMAND_CONTROL_API_URL,
);

type Household = HouseholdRecord;
type AreaSystem = AreaSystemRecord;
type Attachment = AttachmentRecord;
type AttachmentEntityType = Attachment["entityType"];
type Project = Omit<ProjectRecord, "budgetCents"> & { budget?: number };
type Professional = ProfessionalRecord;

type NavItem = {
  id: "dashboard" | "projects" | "maintenance" | "schedule" | "pros";
  icon: ComponentType<{ size?: number }>;
  label: string;
  path: string;
};

const navItems: NavItem[] = [
  { id: "dashboard", icon: LayoutDashboardIcon, label: "Dashboard", path: "/dashboard" },
  { id: "maintenance", icon: WrenchIcon, label: "Maintenance", path: "/maintenance" },
  { id: "schedule", icon: CalendarDaysIcon, label: "Schedule", path: "/schedule" },
  { id: "pros", icon: UsersIcon, label: "Professionals", path: "/pros" },
  { id: "projects", icon: HammerIcon, label: "Projects", path: "/projects" },
];

const today = new Date().toISOString().slice(0, 10);

function toProject(record: ProjectRecord): Project {
  const project: Project = {
    areaSystemId: record.areaSystemId,
    createdAt: record.createdAt,
    householdId: record.householdId,
    id: record.id,
    name: record.name,
    notes: record.notes,
    status: record.status,
    updatedAt: record.updatedAt,
  };

  if (record.budgetCents !== undefined) {
    project.budget = record.budgetCents / 100;
  }

  return project;
}

function areaName(areaSystems: AreaSystem[], areaSystemId?: string): string {
  if (!areaSystemId || areaSystemId === "whole-house") {
    return "Whole House";
  }

  return areaSystems.find((area) => area.id === areaSystemId)?.name ?? "No area";
}

function formatMoney(value: number | undefined): string {
  return new Intl.NumberFormat("en-US", { currency: "USD", maximumFractionDigits: 0, style: "currency" }).format(value ?? 0);
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(new Date(`${value.slice(0, 10)}T12:00:00`));
}

function formatByteSize(value: number | undefined): string {
  if (value === undefined) {
    return "No size";
  }

  if (value < 1024) {
    return `${value} B`;
  }

  if (value < 1024 * 1024) {
    return `${(value / 1024).toFixed(1)} KB`;
  }

  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function isViewableAttachment(contentType: string): boolean {
  return contentType.startsWith("image/") || contentType === "application/pdf";
}

const extensionMimeTypes: Record<string, string> = {
  "7z": "application/x-7z-compressed",
  avi: "video/x-msvideo",
  bmp: "image/bmp",
  csv: "text/csv",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  gif: "image/gif",
  glb: "model/gltf-binary",
  gltf: "model/gltf+json",
  gz: "application/gzip",
  heic: "image/heic",
  jpeg: "image/jpeg",
  jpg: "image/jpeg",
  json: "application/json",
  mov: "video/quicktime",
  mp3: "audio/mpeg",
  mp4: "video/mp4",
  obj: "model/obj",
  pdf: "application/pdf",
  png: "image/png",
  rar: "application/vnd.rar",
  step: "model/step",
  stl: "model/stl",
  stp: "model/step",
  svg: "image/svg+xml",
  tar: "application/x-tar",
  tif: "image/tiff",
  tiff: "image/tiff",
  txt: "text/plain",
  webm: "video/webm",
  webp: "image/webp",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  zip: "application/zip",
};

function inferMimeFromExtension(filename: string): string | undefined {
  const extension = filename.toLowerCase().split(".").pop();
  return extension ? extensionMimeTypes[extension] : undefined;
}

async function inferMimeFromMagicNumber(file: File): Promise<string | undefined> {
  const bytes = new Uint8Array(await file.slice(0, 16).arrayBuffer());
  const startsWith = (signature: number[]) => signature.every((byte, index) => bytes[index] === byte);

  if (startsWith([0x25, 0x50, 0x44, 0x46])) return "application/pdf";
  if (startsWith([0x89, 0x50, 0x4e, 0x47])) return "image/png";
  if (startsWith([0xff, 0xd8, 0xff])) return "image/jpeg";
  if (startsWith([0x47, 0x49, 0x46, 0x38])) return "image/gif";
  if (startsWith([0x50, 0x4b, 0x03, 0x04])) return "application/zip";
  if (startsWith([0x1f, 0x8b])) return "application/gzip";
  if (startsWith([0x52, 0x61, 0x72, 0x21])) return "application/vnd.rar";
  if (startsWith([0x37, 0x7a, 0xbc, 0xaf, 0x27, 0x1c])) return "application/x-7z-compressed";
  if (startsWith([0x42, 0x4d])) return "image/bmp";

  return undefined;
}

async function detectFileMimeType(file: File): Promise<string> {
  return file.type || inferMimeFromExtension(file.name) || (await inferMimeFromMagicNumber(file)) || "application/octet-stream";
}

function toLocalDate(value: string): Date {
  return new Date(`${value.slice(0, 10)}T12:00:00`);
}

function toDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function startOfWeek(date: Date): Date {
  return addDays(date, -date.getDay());
}

function getMonthGrid(anchor: Date): Date[] {
  const firstOfMonth = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const gridStart = startOfWeek(firstOfMonth);
  return Array.from({ length: 42 }, (_, index) => addDays(gridStart, index));
}

function itemsForDate(items: DatedHouseholdItem[], date: Date): DatedHouseholdItem[] {
  const key = toDateKey(date);
  return items.filter((item) => item.date.slice(0, 10) === key);
}

function itemHour(item: DatedHouseholdItem): number | undefined {
  if (!item.date.includes("T")) {
    return undefined;
  }

  return new Date(item.date).getHours();
}

function typeLabel(type: string): string {
  switch (type) {
    case "interaction":
      return "Appointment";
    case "maintenance":
      return "Maintenance";
    default:
      return type[0]?.toUpperCase() + type.slice(1);
  }
}

function titleCase(value: string): string {
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}

type ProjectListProps = {
  areaSystems: AreaSystem[];
  areaFilter: string;
  filteredProjects: Project[];
  openProjectModal: () => void;
  statusFilter: ProjectStatus | "All";
  setAreaFilter: (value: string) => void;
  setStatusFilter: (value: ProjectStatus | "All") => void;
};

type DetailTab = "overview" | "todos" | "supplies" | "permits" | "pros" | "costs" | "attachments" | "notes";

function ProjectListPage({ areaSystems, areaFilter, filteredProjects, openProjectModal, statusFilter, setAreaFilter, setStatusFilter }: ProjectListProps) {
  return (
    <section className="project-browser project-list-page">
      <div className="surface-header compact project-list-header">
        <div>
          <div className="eyebrow">Renovation</div>
          <h1>Projects</h1>
        </div>
        <button className="icon-button primary-action project-create-button" onClick={openProjectModal} title="Create project" type="button">
          <PlusIcon size={16} />
        </button>
      </div>
      <div className="filters">
        <select aria-label="Status filter" onChange={(event) => setStatusFilter(event.target.value as ProjectStatus | "All")} value={statusFilter}>
          {["All", "Planning", "In Progress", "Complete"].map((status) => (
            <option key={status} value={status}>{status}</option>
          ))}
        </select>
        <select aria-label="Area filter" onChange={(event) => setAreaFilter(event.target.value)} value={areaFilter}>
          <option value="All">All areas</option>
          {areaSystems.map((area) => (
            <option key={area.id} value={area.id}>{area.name}</option>
          ))}
        </select>
      </div>
      <div className="project-table project-index-list">
        {filteredProjects.map((project) => (
          <Link className="project-card project-row" key={project.id} to={`/projects/${project.id}`}>
            <small>{project.status}</small>
            <strong>{project.name}</strong>
            <span>{areaName(areaSystems, project.areaSystemId)} · {formatMoney(project.budget)}</span>
            <small>Updated {formatDate(project.updatedAt)}</small>
          </Link>
        ))}
      </div>
    </section>
  );
}

type ProjectDetailPageProps = {
  areaSystems: AreaSystem[];
  attachments: Attachment[];
  detailTab: DetailTab;
  interactions: ProfessionalInteraction[];
  invoices: Invoice[];
  openProjectProfessionalEditModal: (interaction: ProfessionalInteraction) => void;
  openProjectProfessionalModal: (projectId: string) => void;
  openAttachmentViewModal: (attachment: Attachment) => void;
  openAttachmentModal: (project: Project) => void;
  openPermitModal: (projectId: string) => void;
  openSupplyEditModal: (supply: ProjectSupply) => void;
  openSupplyModal: (projectId: string) => void;
  openTodoModal: (projectId: string) => void;
  openTodoEditModal: (todo: ProjectTodo) => void;
  permits: Permit[];
  professionals: Professional[];
  projects: Project[];
  quotes: ProjectQuote[];
  setDetailTab: (tab: DetailTab) => void;
  supplies: ProjectSupply[];
  todos: ProjectTodo[];
  updateProjectLocation: (project: Project, areaSystemName: string) => Promise<void>;
  updateProjectName: (project: Project, name: string) => Promise<void>;
  updateProjectStatus: (projectId: string, status: ProjectStatus) => void;
  updateTodoStatus: (todoId: string, status: ProjectTodo["status"]) => void;
};

function ProjectDetailPage({
  areaSystems,
  attachments,
  detailTab,
  interactions,
  invoices,
  openProjectProfessionalEditModal,
  openProjectProfessionalModal,
  openAttachmentViewModal,
  openAttachmentModal,
  openPermitModal,
  openSupplyEditModal,
  openSupplyModal,
  openTodoModal,
  openTodoEditModal,
  permits,
  professionals,
  projects,
  quotes,
  setDetailTab,
  supplies,
  todos,
  updateProjectLocation,
  updateProjectName,
  updateProjectStatus,
  updateTodoStatus,
}: ProjectDetailPageProps) {
  const { projectId } = useParams();
  const [todoTab, setTodoTab] = useState<"active" | "completed">("active");
  const [locationEditing, setLocationEditing] = useState(false);
  const [locationDraft, setLocationDraft] = useState("");
  const [titleEditing, setTitleEditing] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const selectedProject = projects.find((project) => project.id === projectId);

  if (!selectedProject) {
    return (
      <section className="panel full-panel">
        <div className="empty-state">
          <FileTextIcon size={18} />
          <span>Project not found.</span>
        </div>
        <Link className="icon-text-button back-link" to="/projects">
          <ListIcon size={15} />
          Projects
        </Link>
      </section>
    );
  }

  const projectForInlineEdits = selectedProject;
  const projectTodos = todos.filter((todo) => todo.projectId === selectedProject.id);
  const activeTodos = projectTodos.filter((todo) => todo.status !== "Done");
  const completedTodos = projectTodos.filter((todo) => todo.status === "Done");
  const visibleTodos = todoTab === "active" ? activeTodos : completedTodos;
  const projectSupplies = supplies.filter((supply) => supply.projectId === selectedProject.id);
  const projectPermits = permits.filter((permit) => permit.projectId === selectedProject.id);
  const projectInteractions = interactions.filter((interaction) => interaction.projectId === selectedProject.id);
  const projectQuotes = quotes.filter((quote) => quote.projectId === selectedProject.id);
  const projectInvoices = invoices.filter((invoice) => invoice.projectId === selectedProject.id);
  const projectAttachments = attachments.filter((attachment) => attachment.entityType === "project" && attachment.entityId === selectedProject.id);
  const projectCosts = rollupProjectCosts({
    budget: selectedProject.budget,
    invoices: projectInvoices,
    quotes: projectQuotes,
    supplies: projectSupplies,
  });
  const projectAreaSystems = areaSystems.filter((area) => area.householdId === selectedProject.householdId);
  const projectLocationName = areaName(projectAreaSystems, selectedProject.areaSystemId);

  async function saveInlineLocation() {
    const nextLocation = locationDraft.trim();

    if (!locationEditing) {
      return;
    }

    if (!nextLocation || nextLocation === projectLocationName) {
      setLocationEditing(false);
      setLocationDraft("");
      return;
    }

    try {
      await updateProjectLocation(projectForInlineEdits, nextLocation);
    } finally {
      setLocationEditing(false);
      setLocationDraft("");
    }
  }

  async function saveInlineTitle() {
    const nextTitle = titleDraft.trim();

    if (!titleEditing) {
      return;
    }

    if (!nextTitle || nextTitle === projectForInlineEdits.name) {
      setTitleEditing(false);
      setTitleDraft("");
      return;
    }

    try {
      await updateProjectName(projectForInlineEdits, nextTitle);
    } finally {
      setTitleEditing(false);
      setTitleDraft("");
    }
  }

  return (
    <section className="project-detail project-detail-page">
      <div className="detail-heading">
        <div>
          <Link className="detail-back" to="/projects">
            <ListIcon size={15} />
            Projects
          </Link>
          {locationEditing ? (
            <>
              <input
                aria-label="Project location"
                autoFocus
                className="inline-edit-input eyebrow-inline-input"
                list="project-inline-location-options"
                onBlur={() => {
                  void saveInlineLocation();
                }}
                onChange={(event) => setLocationDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    event.preventDefault();
                    setLocationEditing(false);
                    setLocationDraft("");
                  }

                  if (event.key === "Enter") {
                    event.preventDefault();
                    event.currentTarget.blur();
                  }
                }}
                value={locationDraft}
              />
              <datalist id="project-inline-location-options">
                {projectAreaSystems.map((area) => (
                  <option key={area.id} value={area.name} />
                ))}
              </datalist>
            </>
          ) : (
            <button
              className="eyebrow inline-edit-trigger"
              onClick={() => {
                setLocationDraft(projectLocationName);
                setLocationEditing(true);
              }}
              title="Edit location"
              type="button"
            >
              {projectLocationName}
            </button>
          )}
          {titleEditing ? (
            <input
              aria-label="Project title"
              autoFocus
              className="inline-edit-input title-inline-input"
              onBlur={() => {
                void saveInlineTitle();
              }}
              onChange={(event) => setTitleDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  event.preventDefault();
                  setTitleEditing(false);
                  setTitleDraft("");
                }

                if (event.key === "Enter") {
                  event.preventDefault();
                  event.currentTarget.blur();
                }
              }}
              value={titleDraft}
            />
          ) : (
            <h1>
              <button
                className="inline-title-trigger"
                onClick={() => {
                  setTitleDraft(selectedProject.name);
                  setTitleEditing(true);
                }}
                title="Edit project title"
                type="button"
              >
                {selectedProject.name}
              </button>
            </h1>
          )}
        </div>
        <div className="detail-actions project-status-actions">
          <select
            aria-label="Project status"
            onChange={(event) => updateProjectStatus(selectedProject.id, event.target.value as ProjectStatus)}
            value={selectedProject.status}
          >
            {["Planning", "In Progress", "Complete"].map((status) => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="detail-tabs-bar">
        <div className="tab-row" role="tablist">
          {["overview", "todos", "supplies", "permits", "pros", "costs", "attachments", "notes"].map((tab) => (
            <button className={detailTab === tab ? "is-active" : ""} key={tab} onClick={() => setDetailTab(tab as DetailTab)} type="button">
              {tab}
            </button>
          ))}
        </div>
        {detailTab === "todos" || detailTab === "supplies" || detailTab === "permits" || detailTab === "pros" || detailTab === "attachments" ? (
          <button
            aria-label={detailTab === "todos" ? "Add todo" : detailTab === "supplies" ? "Add supply" : detailTab === "permits" ? "Add permit" : detailTab === "pros" ? "Add pro" : "Add attachment"}
            className="icon-button primary-action tab-action-button"
            onClick={() => {
              if (detailTab === "todos") {
                openTodoModal(selectedProject.id);
              } else if (detailTab === "supplies") {
                openSupplyModal(selectedProject.id);
              } else if (detailTab === "permits") {
                openPermitModal(selectedProject.id);
              } else if (detailTab === "pros") {
                openProjectProfessionalModal(selectedProject.id);
              } else {
                openAttachmentModal(selectedProject);
              }
            }}
            title={detailTab === "todos" ? "Add todo" : detailTab === "supplies" ? "Add supply" : detailTab === "permits" ? "Add permit" : detailTab === "pros" ? "Add pro" : "Add attachment"}
            type="button"
          >
            <PlusIcon size={16} />
          </button>
        ) : null}
      </div>

      <div className="tab-surface">
        {detailTab === "overview" ? (
          <div className="overview-grid">
            <div className="detail-summary overview-summary">
              <div className="metric"><span>Budget</span><strong>{formatMoney(selectedProject.budget)}</strong></div>
              <div className="metric"><span>Actual</span><strong>{formatMoney(projectCosts.actualCost)}</strong></div>
              <div className="metric"><span>Variance</span><strong>{formatMoney(projectCosts.variance)}</strong></div>
            </div>
            <p>{selectedProject.notes}</p>
          </div>
        ) : null}

        {detailTab === "todos" ? (
          <div className="todo-panel">
            <div className="todo-tabs" role="tablist" aria-label="Todo status">
              {([
                ["active", activeTodos.length],
                ["completed", completedTodos.length],
              ] as const).map(([tab, count]) => (
                <button
                  aria-selected={todoTab === tab}
                  className={todoTab === tab ? "is-active" : ""}
                  key={tab}
                  onClick={() => setTodoTab(tab)}
                  role="tab"
                  type="button"
                >
                  {titleCase(tab)}
                  <span>{count}</span>
                </button>
              ))}
            </div>
            <div className="item-list">
              {visibleTodos.length === 0 ? (
                <div className="empty-state">
                  <ClipboardListIcon size={18} />
                  <span>{todoTab === "active" ? "No active todos." : "No completed todos."}</span>
                </div>
              ) : null}
              {visibleTodos.map((todo) => {
                const completed = todo.status === "Done";
                return (
                  <div className={completed ? "work-item static todo-item is-complete" : "work-item static todo-item"} key={todo.id}>
                    <span className="type-dot todo" />
                    <button className="todo-item-copy" onClick={() => openTodoEditModal(todo)} type="button">
                      <strong>{todo.title}</strong>
                      <small>{todo.status} · {todo.dueDate ? formatDate(todo.dueDate) : "No date"}</small>
                    </button>
                    <button
                      aria-label={completed ? `Mark ${todo.title} incomplete` : `Mark ${todo.title} complete`}
                      className="ghost-icon-button todo-toggle"
                      onClick={() => {
                        void updateTodoStatus(todo.id, completed ? "Not Started" : "Done");
                      }}
                      title={completed ? "Mark incomplete" : "Mark complete"}
                      type="button"
                    >
                      {completed ? <RotateCcwIcon size={16} /> : <CheckIcon size={16} />}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        {detailTab === "supplies" ? (
          <div className="item-list">
            {projectSupplies.length === 0 ? (
              <div className="empty-state">
                <HammerIcon size={18} />
                <span>No supplies yet.</span>
              </div>
            ) : null}
            {projectSupplies.map((supply) => (
              <button className="work-item supply-item" key={supply.id} onClick={() => openSupplyEditModal(supply)} type="button">
                <span className="type-dot supply" />
                <strong>{supply.itemName}</strong>
                <small>{supply.quantity} · {supply.status} · {formatMoney(supply.actualCost ?? supply.estimatedCost)}</small>
              </button>
            ))}
          </div>
        ) : null}
        {detailTab === "permits" ? (
          <div className="item-list">
            {projectPermits.length === 0 ? (
              <div className="empty-state">
                <FileTextIcon size={18} />
                <span>No permits yet.</span>
              </div>
            ) : null}
            {projectPermits.map((permit) => (
              <Link className="work-item permit-item" key={permit.id} to={`/projects/${selectedProject.id}/permits/${permit.id}`}>
                <span className="type-dot permit" />
                <strong>{permit.title}</strong>
                <small>{permit.status} · {permit.submissionDueDate ? formatDate(permit.submissionDueDate) : "No date"}</small>
              </Link>
            ))}
          </div>
        ) : null}
        {detailTab === "pros" ? (
          <div className="item-list">
            {projectInteractions.length === 0 ? (
              <div className="empty-state">
                <UsersIcon size={18} />
                <span>No pros attached yet.</span>
              </div>
            ) : null}
            {projectInteractions.map((visit) => {
              const professional = professionals.find((pro) => pro.id === visit.professionalId);
              return (
                <button className="work-item project-pro-item" key={visit.id} onClick={() => openProjectProfessionalEditModal(visit)} type="button">
                  <span className="type-dot interaction" />
                  <strong>{professional ? `${professional.name} · ${visit.title}` : visit.title}</strong>
                  <small>{visit.type} · {visit.status} · {formatDate(visit.startsAt)}</small>
                </button>
              );
            })}
          </div>
        ) : null}
        {detailTab === "costs" ? <div className="cost-grid">{Object.entries(projectCosts).map(([key, value]) => <div className="metric" key={key}><span>{key.replace(/[A-Z]/g, " $&")}</span><strong>{formatMoney(value)}</strong></div>)}</div> : null}
        {detailTab === "attachments" ? (
          <div className="item-list">
            {projectAttachments.length === 0 ? (
              <div className="empty-state">
                <PaperclipIcon size={18} />
                <span>No attachments yet.</span>
              </div>
            ) : null}
            {projectAttachments.map((attachment) => (
              <button className="work-item attachment-item" key={attachment.id} onClick={() => openAttachmentViewModal(attachment)} type="button">
                <span className="type-dot attachment" />
                <strong>{attachment.filename}</strong>
                <small>{attachment.contentType ?? "No content type"} · {formatByteSize(attachment.byteSize)}</small>
              </button>
            ))}
          </div>
        ) : null}
        {detailTab === "notes" ? <textarea className="notes-area" readOnly value={selectedProject.notes} /> : null}
      </div>
    </section>
  );
}

type PermitDetailPageProps = {
  attachments: Attachment[];
  createPermitDocumentAttachment: (permit: Permit, project: Project, file: File) => Promise<void>;
  createPermitMarkdownAttachment: (permit: Permit, project: Project, title: string, markdown: string) => Promise<void>;
  deletePermitById: (permitId: string) => Promise<void>;
  openAttachmentViewModal: (attachment: Attachment) => void;
  permits: Permit[];
  projects: Project[];
  updatePermitDetails: (permitId: string, input: { approvalDeadline?: string; status: Permit["status"]; submissionDueDate?: string; title: string }) => Promise<void>;
};

function PermitDetailPage({
  attachments,
  createPermitDocumentAttachment,
  createPermitMarkdownAttachment,
  deletePermitById,
  openAttachmentViewModal,
  permits,
  projects,
  updatePermitDetails,
}: PermitDetailPageProps) {
  const { permitId, projectId } = useParams();
  const navigate = useNavigate();
  const permit = permits.find((currentPermit) => currentPermit.id === permitId);
  const project = projects.find((currentProject) => currentProject.id === projectId);
  const [editingField, setEditingField] = useState<"title" | "status" | "submissionDueDate" | "approvalDeadline" | null>(null);
  const [titleDraft, setTitleDraft] = useState(permit?.title ?? "");
  const [statusDraft, setStatusDraft] = useState<Permit["status"]>(permit?.status ?? "Needed");
  const [submissionDueDateDraft, setSubmissionDueDateDraft] = useState(permit?.submissionDueDate ?? "");
  const [approvalDeadlineDraft, setApprovalDeadlineDraft] = useState(permit?.approvalDeadline ?? "");
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [noteTitle, setNoteTitle] = useState("");
  const [noteText, setNoteText] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const permitAttachments = attachments.filter((attachment) => attachment.entityType === "permit" && attachment.entityId === permit?.id);

  useEffect(() => {
    if (!permit) {
      return;
    }

    setTitleDraft(permit.title);
    setStatusDraft(permit.status);
    setSubmissionDueDateDraft(permit.submissionDueDate ?? "");
    setApprovalDeadlineDraft(permit.approvalDeadline ?? "");
  }, [permit]);

  if (!permit || !project) {
    return (
      <section className="panel full-panel">
        <div className="empty-state">
          <FileTextIcon size={18} />
          <span>Permit not found.</span>
        </div>
        <Link className="icon-text-button back-link" to={projectId ? `/projects/${projectId}` : "/projects"}>
          <ListIcon size={15} />
          Project
        </Link>
      </section>
    );
  }

  const selectedPermit = permit;
  const selectedProject = project;

  async function savePermitPatch(input: Partial<Pick<Permit, "approvalDeadline" | "status" | "submissionDueDate" | "title">>) {
    setSaving(true);
    setError("");
    try {
      const hasApprovalDeadline = Object.hasOwn(input, "approvalDeadline");
      const hasSubmissionDueDate = Object.hasOwn(input, "submissionDueDate");
      const nextApprovalDeadline = hasApprovalDeadline ? input.approvalDeadline : selectedPermit.approvalDeadline;
      const nextSubmissionDueDate = hasSubmissionDueDate ? input.submissionDueDate : selectedPermit.submissionDueDate;

      await updatePermitDetails(selectedPermit.id, {
        ...(nextApprovalDeadline ? { approvalDeadline: nextApprovalDeadline } : {}),
        status: input.status ?? selectedPermit.status,
        ...(nextSubmissionDueDate ? { submissionDueDate: nextSubmissionDueDate } : {}),
        title: input.title ?? selectedPermit.title,
      });
    } catch {
      setError("Could not save.");
    } finally {
      setEditingField(null);
      setSaving(false);
    }
  }

  async function saveTitleDraft() {
    const nextTitle = titleDraft.trim();

    if (!nextTitle) {
      setError("Title required.");
      return;
    }

    if (nextTitle === selectedPermit.title) {
      setEditingField(null);
      return;
    }

    await savePermitPatch({ title: nextTitle });
  }

  async function saveDateDraft(field: "approvalDeadline" | "submissionDueDate", value: string) {
    if (value === (selectedPermit[field] ?? "")) {
      setEditingField(null);
      return;
    }

    await savePermitPatch({ [field]: value || undefined });
  }

  async function deleteCurrentPermit() {
    if (!window.confirm("Delete this permit?")) {
      return;
    }

    setSaving(true);
    setError("");
    try {
      await deletePermitById(selectedPermit.id);
      navigate(`/projects/${selectedProject.id}`);
    } catch {
      setError("Could not delete.");
      setSaving(false);
    }
  }

  async function addMarkdownNote() {
    const nextText = noteText.trim();
    if (!nextText) {
      setError("Note required.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      await createPermitMarkdownAttachment(selectedPermit, selectedProject, noteTitle.trim() || "Permit note", nextText);
      setNoteTitle("");
      setNoteText("");
      setNoteModalOpen(false);
    } catch {
      setError("Could not save note.");
    } finally {
      setSaving(false);
    }
  }

  async function attachDocument(file: File | undefined) {
    if (!file) {
      return;
    }

    setSaving(true);
    setError("");
    try {
      await createPermitDocumentAttachment(selectedPermit, selectedProject, file);
    } catch {
      setError("Could not attach document.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="project-detail permit-detail-page">
      <div className="detail-heading permit-page-heading">
        <div>
          <Link className="detail-back" to={`/projects/${selectedProject.id}`}>
            <ListIcon size={15} />
            {selectedProject.name}
          </Link>
          <div className="eyebrow">Permit</div>
          {editingField === "title" ? (
            <input
              aria-label="Permit title"
              autoFocus
              className="inline-edit-input title-inline-input"
              onBlur={() => {
                void saveTitleDraft();
              }}
              onChange={(event) => setTitleDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  event.preventDefault();
                  setEditingField(null);
                  setTitleDraft(selectedPermit.title);
                }

                if (event.key === "Enter") {
                  event.preventDefault();
                  event.currentTarget.blur();
                }
              }}
              value={titleDraft}
            />
          ) : (
            <h1>
              <button
                className="inline-title-trigger"
                onClick={() => {
                  setTitleDraft(selectedPermit.title);
                  setEditingField("title");
                }}
                title="Edit permit title"
                type="button"
              >
                {selectedPermit.title}
              </button>
            </h1>
          )}
        </div>
        <div className="overflow-menu permit-page-actions">
          <button
            aria-expanded={menuOpen}
            aria-label="Permit actions"
            className="ghost-icon-button"
            disabled={saving}
            onClick={() => setMenuOpen((open) => !open)}
            title="Permit actions"
            type="button"
          >
            <MoreHorizontalIcon size={17} />
          </button>
          {menuOpen ? (
            <div className="overflow-menu-content">
              <button
                className="danger-menu-action"
                onClick={() => {
                  setMenuOpen(false);
                  void deleteCurrentPermit();
                }}
                type="button"
              >
                Delete
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="permit-view-grid">
        <div className="metric permit-inline-field">
          <span>Status</span>
          {editingField === "status" ? (
            <select
              autoFocus
              onBlur={() => setEditingField(null)}
              onChange={(event) => {
                const nextStatus = event.target.value as Permit["status"];
                setStatusDraft(nextStatus);
                void savePermitPatch({ status: nextStatus });
              }}
              value={statusDraft}
            >
              {["Needed", "Drafting", "Submitted", "Approved", "Rejected", "Closed"].map((permitStatusOption) => (
                <option key={permitStatusOption} value={permitStatusOption}>{permitStatusOption}</option>
              ))}
            </select>
          ) : (
            <button
              className="inline-value-trigger"
              onClick={() => {
                setStatusDraft(selectedPermit.status);
                setEditingField("status");
              }}
              type="button"
            >
              {selectedPermit.status}
            </button>
          )}
        </div>
        <div className="metric permit-inline-field">
          <span>Submit</span>
          {editingField === "submissionDueDate" ? (
            <input
              autoFocus
              onBlur={() => {
                void saveDateDraft("submissionDueDate", submissionDueDateDraft);
              }}
              onChange={(event) => setSubmissionDueDateDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  event.preventDefault();
                  setEditingField(null);
                  setSubmissionDueDateDraft(selectedPermit.submissionDueDate ?? "");
                }

                if (event.key === "Enter") {
                  event.preventDefault();
                  event.currentTarget.blur();
                }
              }}
              type="date"
              value={submissionDueDateDraft}
            />
          ) : (
            <button
              className="inline-value-trigger"
              onClick={() => {
                setSubmissionDueDateDraft(selectedPermit.submissionDueDate ?? "");
                setEditingField("submissionDueDate");
              }}
              type="button"
            >
              {selectedPermit.submissionDueDate ? formatDate(selectedPermit.submissionDueDate) : "None"}
            </button>
          )}
        </div>
        <div className="metric permit-inline-field">
          <span>Approval</span>
          {editingField === "approvalDeadline" ? (
            <input
              autoFocus
              onBlur={() => {
                void saveDateDraft("approvalDeadline", approvalDeadlineDraft);
              }}
              onChange={(event) => setApprovalDeadlineDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  event.preventDefault();
                  setEditingField(null);
                  setApprovalDeadlineDraft(selectedPermit.approvalDeadline ?? "");
                }

                if (event.key === "Enter") {
                  event.preventDefault();
                  event.currentTarget.blur();
                }
              }}
              type="date"
              value={approvalDeadlineDraft}
            />
          ) : (
            <button
              className="inline-value-trigger"
              onClick={() => {
                setApprovalDeadlineDraft(selectedPermit.approvalDeadline ?? "");
                setEditingField("approvalDeadline");
              }}
              type="button"
            >
              {selectedPermit.approvalDeadline ? formatDate(selectedPermit.approvalDeadline) : "None"}
            </button>
          )}
        </div>
      </div>

      {error ? <p className="auth-error permit-page-error">{error}</p> : null}

      <div className="permit-attachment-toolbar">
        <label className="icon-text-button permit-file-action">
          <FileUpIcon size={16} />
          Document
          <input
            onChange={(event) => {
              void attachDocument(event.target.files?.[0]);
              event.currentTarget.value = "";
            }}
            type="file"
          />
        </label>
        <button className="icon-text-button" onClick={() => setNoteModalOpen(true)} type="button">
          <StickyNoteIcon size={16} />
          Text
        </button>
      </div>

      <div className="item-list permit-attachment-list">
        {permitAttachments.length === 0 ? (
          <div className="empty-state">
            <PaperclipIcon size={18} />
            <span>No files.</span>
          </div>
        ) : null}
        {permitAttachments.map((attachment) => (
          <button className="work-item attachment-item" key={attachment.id} onClick={() => openAttachmentViewModal(attachment)} type="button">
            <span className="type-dot attachment" />
            <strong>{attachment.filename}</strong>
            <small>{attachment.contentType ?? "No content type"} · {formatByteSize(attachment.byteSize)}</small>
          </button>
        ))}
      </div>

      {noteModalOpen ? (
        <div className="modal-backdrop" role="presentation">
          <form
            aria-labelledby="permit-note-modal-title"
            className="modal-panel permit-note-modal"
            onSubmit={(event) => {
              event.preventDefault();
              void addMarkdownNote();
            }}
          >
            <div className="modal-heading">
              <div>
                <div className="eyebrow">Markdown</div>
                <h2 id="permit-note-modal-title">Add text</h2>
              </div>
              <button className="ghost-icon-button" onClick={() => setNoteModalOpen(false)} title="Close" type="button">
                <XIcon size={17} />
              </button>
            </div>
            <label className="modal-field">
              <span>Title</span>
              <input onChange={(event) => setNoteTitle(event.target.value)} placeholder="Permit note" value={noteTitle} />
            </label>
            <label className="modal-field">
              <span>Text</span>
              <textarea className="markdown-note-area" onChange={(event) => setNoteText(event.target.value)} value={noteText} />
            </label>
            <div className="modal-actions">
              <button className="icon-text-button" onClick={() => setNoteModalOpen(false)} type="button">Cancel</button>
              <button className="icon-text-button primary-action" disabled={saving} type="submit">
                {saving ? <LoaderCircleIcon className="spin-icon" size={16} /> : null}
                Save
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </section>
  );
}

type MaintenanceDetailPageProps = {
  maintenance: MaintenanceItem[];
};

function MaintenanceDetailPage({ maintenance }: MaintenanceDetailPageProps) {
  const { maintenanceId } = useParams();
  const item = maintenance.find((maintenanceItem) => maintenanceItem.id === maintenanceId);

  if (!item) {
    return (
      <section className="panel full-panel">
        <div className="empty-state">
          <WrenchIcon size={18} />
          <span>Maintenance not found.</span>
        </div>
        <Link className="icon-text-button back-link" to="/maintenance">
          <ListIcon size={15} />
          Maintenance
        </Link>
      </section>
    );
  }

  return (
    <section className="project-detail maintenance-detail-page">
      <div className="detail-heading">
        <div>
          <Link className="detail-back" to="/maintenance">
            <ListIcon size={15} />
            Maintenance
          </Link>
          <div className="eyebrow">Maintenance</div>
          <h1>{item.title}</h1>
        </div>
      </div>

      <div className="detail-summary">
        <div className="metric"><span>Status</span><strong>{item.status}</strong></div>
        <div className="metric"><span>Frequency</span><strong>{item.frequency}</strong></div>
        <div className="metric"><span>Next Due</span><strong>{item.nextDueDate ? formatDate(item.nextDueDate) : "None"}</strong></div>
      </div>

      <div className="maintenance-detail-grid">
        <section className="panel">
          <div className="panel-heading"><h2>Schedule</h2></div>
          <div className="metric"><span>Due</span><strong>{item.nextDueDate ? formatDate(item.nextDueDate) : "None"}</strong></div>
        </section>
      </div>
    </section>
  );
}

type ProfessionalDetailPageProps = {
  professionals: Professional[];
  updateProfessional: (professionalId: string, updates: Partial<Professional>) => void;
};

function ProfessionalDetailPage({ professionals, updateProfessional }: ProfessionalDetailPageProps) {
  const { professionalId } = useParams();
  const professional = professionals.find((pro) => pro.id === professionalId);
  const [editing, setEditing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  if (!professional) {
    return (
      <section className="panel full-panel">
        <div className="empty-state">
          <UsersIcon size={18} />
          <span>Professional not found.</span>
        </div>
        <Link className="icon-text-button back-link" to="/pros">
          <ListIcon size={15} />
          Professionals
        </Link>
      </section>
    );
  }

  return (
    <section className="project-detail professional-detail-page">
      <div className="detail-heading">
        <div>
          <Link className="detail-back" to="/pros">
            <ListIcon size={15} />
            Professionals
          </Link>
          <div className="eyebrow">{professional.specialty}</div>
          <h1>{professional.name}</h1>
        </div>
        <div className="overflow-menu">
          <button
            aria-expanded={menuOpen}
            aria-label="Professional actions"
            className="ghost-icon-button"
            onClick={() => setMenuOpen((open) => !open)}
            title="Professional actions"
            type="button"
          >
            <MoreHorizontalIcon size={17} />
          </button>
          {menuOpen ? (
            <div className="overflow-menu-content">
              <button
                onClick={() => {
                  setEditing((current) => !current);
                  setMenuOpen(false);
                }}
                type="button"
              >
                {editing ? "Done" : "Edit"}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="detail-summary">
        <div className="metric"><span>Company</span><strong>{professional.company}</strong></div>
        <div className="metric"><span>Phone</span><strong>{professional.phone}</strong></div>
        <div className="metric"><span>Email</span><strong>{professional.email}</strong></div>
      </div>

      {editing ? (
        <div className="professional-editor-grid">
          {(["name", "company", "specialty", "phone", "email"] as const).map((field) => (
            <label className="modal-field" key={field}>
              <span>{titleCase(field)}</span>
              <input
                onChange={(event) => updateProfessional(professional.id, { [field]: event.target.value })}
                value={professional[field]}
              />
            </label>
          ))}
        </div>
      ) : (
        <div className="professional-view-grid">
          <div className="metric"><span>Specialty</span><strong>{professional.specialty}</strong></div>
          <div className="metric"><span>Company</span><strong>{professional.company}</strong></div>
        </div>
      )}
    </section>
  );
}

type AuthGateProps = {
  authError: string;
  isSubmitting: boolean;
  signInMessage: string;
  submitSignIn: () => void;
};

type AuthCallbackProps = {
  completeAuthCallback: () => void;
  error: string;
};

function callbackErrorMessage(error: string): string {
  switch (error) {
    case "ATTEMPTS_EXCEEDED":
      return "Too many sign-in attempts. Wait a moment, then try signing in again.";
    case "HANDOFF_FORBIDDEN":
      return "r-auth refused the Command Control handoff. Check the configured audience and allowed origins.";
    case "HANDOFF_UNAUTHORIZED":
      return "r-auth could not find a browser session for the handoff. Start sign-in again.";
    default:
      return error ? `Sign-in failed: ${error}` : "";
  }
}

function authSessionFromToken(token: string, user?: Partial<AuthSession["user"]>): AuthSession {
  const sessionUser: AuthSession["user"] = {
    email: user?.email ?? "",
    emailVerified: user?.emailVerified ?? false,
    id: user?.id ?? "authenticated-user",
    name: user?.name ?? user?.email ?? "",
  };

  if (user?.image !== undefined) {
    sessionUser.image = user.image;
  }

  if (user?.role !== undefined) {
    sessionUser.role = user.role;
  }

  return {
    user: sessionUser,
    session: {
      expiresAt: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
      id: "bearer-session",
      token,
      userId: user?.id ?? "authenticated-user",
    },
  };
}

function userInitial(user: AuthSession["user"]): string {
  return (user.name || user.email || "?").trim().slice(0, 1).toUpperCase();
}

function displayUserName(user: AuthSession["user"]): string {
  return user.name?.trim() || user.email || "Account";
}

function truncateEmail(value: string): string {
  if (!value) {
    return "No email";
  }

  if (value.length <= 22) {
    return value;
  }

  return `${value.slice(0, 21)}...`;
}

function LoadingScreen() {
  return (
    <div className="auth-shell">
      <div className="auth-panel compact-auth-panel">
        <LoaderCircleIcon aria-hidden="true" className="spin-icon" size={20} />
        <strong>Checking session</strong>
      </div>
    </div>
  );
}

function AuthGate({
  authError,
  isSubmitting,
  signInMessage,
  submitSignIn,
}: AuthGateProps) {
  return (
    <div className="auth-shell">
      <form
        className="auth-panel"
        onSubmit={(event) => {
          event.preventDefault();
          submitSignIn();
        }}
      >
        <div className="auth-mark">
          <CommandIcon size={22} />
        </div>
        <div className="auth-copy">
          <div className="eyebrow">Command Center</div>
          <h1>Sign in</h1>
          <p>Continue through r-auth to open your household command center.</p>
        </div>

        {authError ? <p className="auth-error">{authError}</p> : null}
        {signInMessage ? <p className="auth-message">{signInMessage}</p> : null}

        <button className="icon-text-button primary-action auth-submit" disabled={isSubmitting} type="submit">
          {isSubmitting ? <LoaderCircleIcon className="spin-icon" size={16} /> : <LogInIcon size={16} />}
          Continue with r-auth
        </button>
      </form>
    </div>
  );
}

function AuthCallback({ completeAuthCallback, error }: AuthCallbackProps) {
  useEffect(() => {
    void completeAuthCallback();
  }, [completeAuthCallback]);

  return (
    <div className="auth-shell">
      <div className={error ? "auth-panel auth-status-panel" : "auth-panel compact-auth-panel"}>
        {error ? (
          <>
            <div className="auth-mark">
              <KeyRoundIcon aria-hidden="true" size={20} />
            </div>
            <div className="auth-copy">
              <h1>Sign-in handoff stopped</h1>
              <p>{error}</p>
              <p>
                If you can access the r-auth dashboard, your central login is fine. This usually means the
                Command Control handoff did not receive a browser cookie or the origin is not allowed.
              </p>
            </div>
            <Link className="icon-text-button primary-action auth-submit" to="/">
              Return to sign in
            </Link>
          </>
        ) : (
          <>
            <LoaderCircleIcon aria-hidden="true" className="spin-icon" size={20} />
            <strong>Completing sign-in</strong>
          </>
        )}
      </div>
    </div>
  );
}

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthCallback = location.pathname === "/auth/callback";
  const authCallbackInFlight = useRef(false);
  const [selectedHouseholdId, setSelectedHouseholdId] = useState("home-main");
  const [householdPickerOpen, setHouseholdPickerOpen] = useState(false);
  const [householdSearch, setHouseholdSearch] = useState("");
  const [households, setHouseholds] = useState<Household[]>([]);
  const [areaSystems, setAreaSystems] = useState<AreaSystem[]>([]);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [todos, setTodos] = useState<ProjectTodo[]>([]);
  const [maintenance, setMaintenance] = useState<MaintenanceItem[]>([]);
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [permits, setPermits] = useState<Permit[]>([]);
  const [interactions, setInteractions] = useState<ProfessionalInteraction[]>([]);
  const [supplies, setSupplies] = useState<ProjectSupply[]>([]);
  const [quotes, setQuotes] = useState<ProjectQuote[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [scheduleMode, setScheduleMode] = useState<"agenda" | "month" | "week" | "day">("agenda");
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | "All">("All");
  const [areaFilter, setAreaFilter] = useState("All");
  const [newTodoTitle, setNewTodoTitle] = useState("");
  const [newTodoDueDate, setNewTodoDueDate] = useState("");
  const [newTodoStatus, setNewTodoStatus] = useState<ProjectTodo["status"]>("Not Started");
  const [editingTodoId, setEditingTodoId] = useState("");
  const [todoProjectId, setTodoProjectId] = useState("");
  const [todoModalOpen, setTodoModalOpen] = useState(false);
  const [todoCreating, setTodoCreating] = useState(false);
  const [todoCreateError, setTodoCreateError] = useState("");
  const [supplyModalOpen, setSupplyModalOpen] = useState(false);
  const [editingSupplyId, setEditingSupplyId] = useState("");
  const [supplyProjectId, setSupplyProjectId] = useState("");
  const [supplyItemName, setSupplyItemName] = useState("");
  const [supplyQuantity, setSupplyQuantity] = useState("1");
  const [supplyStatus, setSupplyStatus] = useState<ProjectSupply["status"]>("Needed");
  const [supplyEstimatedCost, setSupplyEstimatedCost] = useState("");
  const [supplyActualCost, setSupplyActualCost] = useState("");
  const [supplySaving, setSupplySaving] = useState(false);
  const [supplyError, setSupplyError] = useState("");
  const [permitModalOpen, setPermitModalOpen] = useState(false);
  const [editingPermitId, setEditingPermitId] = useState("");
  const [permitProjectId, setPermitProjectId] = useState("");
  const [permitTitle, setPermitTitle] = useState("");
  const [permitStatus, setPermitStatus] = useState<Permit["status"]>("Needed");
  const [permitSubmissionDueDate, setPermitSubmissionDueDate] = useState("");
  const [permitApprovalDeadline, setPermitApprovalDeadline] = useState("");
  const [permitSaving, setPermitSaving] = useState(false);
  const [permitError, setPermitError] = useState("");
  const [projectProfessionalModalOpen, setProjectProfessionalModalOpen] = useState(false);
  const [editingInteractionId, setEditingInteractionId] = useState("");
  const [projectProfessionalProjectId, setProjectProfessionalProjectId] = useState("");
  const [projectProfessionalMode, setProjectProfessionalMode] = useState<"existing" | "new">("existing");
  const [selectedProfessionalId, setSelectedProfessionalId] = useState("");
  const [projectProfessionalTitle, setProjectProfessionalTitle] = useState("");
  const [projectProfessionalStatus, setProjectProfessionalStatus] = useState<ProfessionalInteraction["status"]>("Scheduled");
  const [projectProfessionalType, setProjectProfessionalType] = useState<ProfessionalInteraction["type"]>("Consultation");
  const [projectProfessionalStartsAt, setProjectProfessionalStartsAt] = useState(`${today}T09:00`);
  const [projectProfessionalDraft, setProjectProfessionalDraft] = useState<Omit<Professional, "id">>({
    company: "",
    email: "",
    name: "",
    phone: "",
    specialty: "",
  });
  const [projectProfessionalSaving, setProjectProfessionalSaving] = useState(false);
  const [projectProfessionalError, setProjectProfessionalError] = useState("");
  const [attachmentModalOpen, setAttachmentModalOpen] = useState(false);
  const [editingAttachmentId, setEditingAttachmentId] = useState("");
  const [attachmentHouseholdId, setAttachmentHouseholdId] = useState("");
  const [attachmentEntityId, setAttachmentEntityId] = useState("");
  const [attachmentFilename, setAttachmentFilename] = useState("");
  const [attachmentDetectedFilename, setAttachmentDetectedFilename] = useState("");
  const [attachmentContentType, setAttachmentContentType] = useState("");
  const [attachmentByteSize, setAttachmentByteSize] = useState("");
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [attachmentEditing, setAttachmentEditing] = useState(false);
  const [attachmentObjectUrl, setAttachmentObjectUrl] = useState("");
  const [attachmentContentLoading, setAttachmentContentLoading] = useState(false);
  const [attachmentSaving, setAttachmentSaving] = useState(false);
  const [attachmentError, setAttachmentError] = useState("");
  const [newMaintenanceTitle, setNewMaintenanceTitle] = useState("");
  const [newMaintenanceFrequency, setNewMaintenanceFrequency] = useState<MaintenanceFrequency>("Monthly");
  const [newMaintenanceDueDate, setNewMaintenanceDueDate] = useState(today);
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectAreaSystemName, setNewProjectAreaSystemName] = useState("");
  const [projectCreating, setProjectCreating] = useState(false);
  const [projectCreateError, setProjectCreateError] = useState("");
  const [householdModalOpen, setHouseholdModalOpen] = useState(false);
  const [newHouseholdName, setNewHouseholdName] = useState("");
  const [newHouseholdSummary, setNewHouseholdSummary] = useState("");
  const [householdCreating, setHouseholdCreating] = useState(false);
  const [householdCreateError, setHouseholdCreateError] = useState("");
  const [maintenanceModalOpen, setMaintenanceModalOpen] = useState(false);
  const [professionalModalOpen, setProfessionalModalOpen] = useState(false);
  const [newProfessional, setNewProfessional] = useState<Omit<Professional, "id">>({
    company: "",
    email: "",
    name: "",
    phone: "",
    specialty: "",
  });
  const [detailTab, setDetailTab] = useState<DetailTab>("overview");
  const [projectMenuOpen, setProjectMenuOpen] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(() => window.matchMedia("(min-width: 821px)").matches);
  const [statusRailOpen, setStatusRailOpen] = useState(true);
  const [backendStatus, setBackendStatus] = useState<"checking" | "online" | "offline">("checking");
  const [authSession, setAuthSession] = useState<AuthSession | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState("");
  const [signInSubmitting, setSignInSubmitting] = useState(false);
  const [signInMessage, setSignInMessage] = useState("");

  const loadHouseholdData = useCallback(async () => {
    const data = await commandControlClient.households.appData.query();
    setAreaSystems(data.areaSystems);
    setAttachments(data.attachments);
    setHouseholds(data.households);
    setInteractions(data.interactions);
    setInvoices(data.invoices);
    setMaintenance(data.maintenance);
    setPermits(data.permits);
    setProfessionals(data.professionals);
    setProjects(data.projects.map(toProject));
    setQuotes(data.quotes);
    setSupplies(data.supplies);
    setTodos(data.todos);
    setBackendStatus("online");
  }, []);

  const selectedHousehold = households.find((household) => household.id === selectedHouseholdId) ?? households[0];
  const selectedHouseholdAreaSystems = areaSystems.filter((area) => area.householdId === selectedHousehold?.id);
  const householdProjects = projects.filter((project) => project.householdId === selectedHousehold?.id);
  const recentProjects = [...householdProjects]
    .sort((left, right) => new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime())
    .slice(0, 4);
  const statusRailProject = householdProjects.find((project) => project.status !== "Complete") ?? householdProjects[0];
  const statusRailCosts = rollupProjectCosts({
    budget: statusRailProject?.budget,
    invoices: invoices.filter((invoice) => invoice.projectId === statusRailProject?.id),
    quotes: quotes.filter((quote) => quote.projectId === statusRailProject?.id),
    supplies: supplies.filter((supply) => supply.projectId === statusRailProject?.id),
  });

  const sources = { interactions, invoices, maintenance, permits, todos };
  const attentionItems = useMemo(() => getNeedsAttention(sources, today), [interactions, invoices, maintenance, permits, todos]);
  const scheduleAnchor = toLocalDate(today);
  const weekStart = startOfWeek(scheduleAnchor);
  const weekDays = Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));
  const weekStartKey = toDateKey(weekDays[0] ?? weekStart);
  const weekEndKey = toDateKey(weekDays[6] ?? addDays(weekStart, 6));
  const monthDays = getMonthGrid(scheduleAnchor);
  const monthStart = new Date(scheduleAnchor.getFullYear(), scheduleAnchor.getMonth(), 1);
  const monthEnd = new Date(scheduleAnchor.getFullYear(), scheduleAnchor.getMonth() + 1, 0);
  const monthLabel = new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" }).format(scheduleAnchor);
  const dayLabel = new Intl.DateTimeFormat("en-US", { weekday: "long", month: "long", day: "numeric" }).format(scheduleAnchor);
  const scheduleItems = useMemo(
    () => getScheduleItems(sources, { from: toDateKey(monthStart), to: toDateKey(monthEnd) }),
    [interactions, invoices, maintenance, permits, todos],
  );
  const weekItems = scheduleItems.filter((item) => item.date.slice(0, 10) >= weekStartKey && item.date.slice(0, 10) <= weekEndKey);
  const dayItems = itemsForDate(scheduleItems, scheduleAnchor);
  const allDayItems = dayItems.filter((item) => itemHour(item) === undefined);
  const dayHours = Array.from({ length: 11 }, (_, index) => index + 8);
  const activeProjects = householdProjects.filter((project) => project.status === "In Progress");
  const visibleHouseholds = households.filter((household) =>
    household.name.toLowerCase().includes(householdSearch.toLowerCase()),
  );

  useEffect(() => {
    function handleSidebarShortcut(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "b") {
        event.preventDefault();
        setSidebarOpen((open) => !open);
      }
    }

    window.addEventListener("keydown", handleSidebarShortcut);
    return () => window.removeEventListener("keydown", handleSidebarShortcut);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadAuthenticatedApp() {
      try {
        if (isAuthCallback) {
          setAuthLoading(false);
          return;
        }

        const storedToken = storedAuthToken();

        const callbackError = new URLSearchParams(window.location.search).get("error");
        if (callbackError) {
          setAuthError(callbackErrorMessage(callbackError));
          window.history.replaceState({}, "", window.location.pathname);
        }

        if (!storedToken) {
          if (!cancelled) {
            setAuthSession(null);
            setAuthLoading(false);
          }
          return;
        }

        setCommandControlAuthToken(storedToken);
        const session = await getAuthSession(storedToken);
        if (!session) {
          throw new Error("Stored r-auth token was rejected.");
        }

        await loadHouseholdData();
        if (!cancelled) {
          setAuthSession(session);
          setAuthLoading(false);
          setBackendStatus("online");
        }
      } catch {
        clearStoredAuthToken();
        setCommandControlAuthToken(null);
        if (!cancelled) {
          setAuthSession(null);
          setAuthLoading(false);
          setAuthError("Your saved Command Control session could not be verified. Please sign in again.");
          setBackendStatus("offline");
        }
      }
    }

    void loadAuthenticatedApp();

    return () => {
      cancelled = true;
    };
  }, [isAuthCallback, loadHouseholdData]);

  useEffect(() => {
    if (households.length > 0 && !households.some((household) => household.id === selectedHouseholdId)) {
      setSelectedHouseholdId(households[0]?.id ?? "");
    }
  }, [households, selectedHouseholdId]);

  function submitSignIn() {
    setAuthError("");
    setSignInMessage("");
    setSignInSubmitting(true);
    window.location.href = authHandoffUrl(`${window.location.origin}/auth/callback`);
  }

  const completeAuthCallback = useCallback(async () => {
    if (authCallbackInFlight.current) {
      return;
    }

    authCallbackInFlight.current = true;
    setAuthError("");

    function loadDataAfterAuth() {
      void loadHouseholdData().catch(() => {
        setBackendStatus("offline");
      });
    }

    try {
      const urlHandoff = parseAuthHandoffUrl(window.location.href);

      if (!urlHandoff) {
        const existingToken = storedAuthToken();
        if (existingToken) {
          setCommandControlAuthToken(existingToken);
          const session = await getAuthSession(existingToken);
          if (!session) {
            throw new Error("Stored r-auth token was rejected.");
          }

          setAuthSession(session);
          setAuthLoading(false);
          navigate("/dashboard", { replace: true });
          loadDataAfterAuth();
          return;
        }

        setAuthSession(null);
        setCommandControlAuthToken(null);
        setAuthError(
          "This sign-in callback reached Command Control without r-auth handoff data. Start sign-in again from Command Control.",
        );
        return;
      }

      const handoff = urlHandoff;

      storeAuthToken(handoff.token, handoff.expiresAt);
      setCommandControlAuthToken(handoff.token);
      const session = await getAuthSession(handoff.token);
      if (!session && !handoff.user) {
        throw new Error("r-auth handoff token could not be verified.");
      }

      setAuthSession(session ?? authSessionFromToken(handoff.token, handoff.user));
      setAuthLoading(false);
      navigate("/dashboard", { replace: true });
      loadDataAfterAuth();
    } catch {
      setAuthError("Could not complete sign-in. Check r-auth trusted origins and handoff settings.");
    } finally {
      setAuthLoading(false);
    }
  }, [loadHouseholdData, navigate]);

  async function signOut() {
    await signOutOfAuth();
    setCommandControlAuthToken(null);
    clearStoredAuthToken();
    setAuthSession(null);
    setHouseholds([]);
    setAreaSystems([]);
    setAttachments([]);
    setProjects([]);
    setTodos([]);
    setMaintenance([]);
    setProfessionals([]);
    setPermits([]);
    setInteractions([]);
    setSupplies([]);
    setQuotes([]);
    setInvoices([]);
    navigate("/", { replace: true });
  }

  const filteredProjects = householdProjects.filter((project) => {
    const matchesStatus = statusFilter === "All" ? project.status !== "Complete" : project.status === statusFilter;
    const matchesArea = areaFilter === "All" || project.areaSystemId === areaFilter;
    return matchesStatus && matchesArea;
  });

  function openHouseholdModal() {
    setHouseholdCreateError("");
    setNewHouseholdName("");
    setNewHouseholdSummary("");
    setHouseholdPickerOpen(false);
    setHouseholdModalOpen(true);
  }

  async function createHousehold() {
    const name = newHouseholdName.trim();

    if (!name) {
      setHouseholdCreateError("Enter a household name.");
      return;
    }

    setHouseholdCreating(true);
    setHouseholdCreateError("");

    try {
      const household = await commandControlClient.households.create.mutate({
        name,
        projectSummary: newHouseholdSummary.trim(),
      });
      setHouseholds((current) => [household, ...current]);
      setSelectedHouseholdId(household.id);
      setHouseholdModalOpen(false);
      setNewHouseholdName("");
      setNewHouseholdSummary("");
    } catch {
      setHouseholdCreateError("Could not create the household. Check your connection and try again.");
    } finally {
      setHouseholdCreating(false);
    }
  }

  function openProjectModal() {
    if (!selectedHousehold) {
      openHouseholdModal();
      return;
    }

    setProjectCreateError("");
    setNewProjectName(`New Project ${householdProjects.length + 1}`);
    setNewProjectAreaSystemName(selectedHouseholdAreaSystems[0]?.name ?? "Whole House");
    setProjectModalOpen(true);
  }

  async function createProject() {
    const name = newProjectName.trim();

    if (!selectedHousehold) {
      setProjectCreateError("Select a household before creating a project.");
      return;
    }

    if (!name) {
      setProjectCreateError("Enter a project name.");
      return;
    }

    setProjectCreating(true);
    setProjectCreateError("");

    try {
      const areaSystemName = newProjectAreaSystemName.trim();
      const matchingAreaSystem = selectedHouseholdAreaSystems.find((area) => area.name.toLowerCase() === areaSystemName.toLowerCase());
      const created = await commandControlClient.households.createProject.mutate({
        householdId: selectedHousehold.id,
        name,
        areaSystemId: matchingAreaSystem?.id ?? "whole-house",
        areaSystemName: areaSystemName || undefined,
      });
      const project = toProject(created);
      if (areaSystemName && !areaSystems.some((area) => area.id === project.areaSystemId)) {
        setAreaSystems((current) => [
          ...current,
          {
            householdId: selectedHousehold.id,
            id: project.areaSystemId,
            kind: "Area",
            name: areaSystemName,
          },
        ]);
      }
      setProjects((current) => [project, ...current]);
      setProjectModalOpen(false);
      setNewProjectName("");
      setNewProjectAreaSystemName("");
      navigate(`/projects/${project.id}`);
    } catch {
      setProjectCreateError("Could not create the project. Check your connection and try again.");
    } finally {
      setProjectCreating(false);
    }
  }

  async function saveProjectFields(projectId: string, name: string | undefined, areaSystemName: string | undefined) {
    const trimmedAreaSystemName = areaSystemName?.trim();
    const existingProject = projects.find((currentProject) => currentProject.id === projectId);
    const projectAreaSystems = areaSystems.filter((area) => area.householdId === existingProject?.householdId);
    const matchingAreaSystem = projectAreaSystems.find((area) => area.name.toLowerCase() === trimmedAreaSystemName?.toLowerCase());
    const updated = await commandControlClient.households.updateProject.mutate({
      areaSystemId: matchingAreaSystem?.id,
      areaSystemName: trimmedAreaSystemName || undefined,
      name: name?.trim(),
      projectId,
    });
    const project = toProject(updated);

    if (trimmedAreaSystemName && !areaSystems.some((area) => area.id === project.areaSystemId)) {
      setAreaSystems((current) => [
        ...current,
        {
          householdId: project.householdId,
          id: project.areaSystemId,
          kind: "Area",
          name: trimmedAreaSystemName,
        },
      ]);
    }

    setProjects((current) => current.map((currentProject) => (currentProject.id === project.id ? project : currentProject)));
  }

  async function updateProjectLocation(project: Project, areaSystemName: string) {
    await saveProjectFields(project.id, project.name, areaSystemName);
  }

  async function updateProjectName(project: Project, name: string) {
    await saveProjectFields(project.id, name, undefined);
  }

  async function updateProjectStatus(projectId: string, status: ProjectStatus) {
    const updated = await commandControlClient.households.updateProjectStatus.mutate({ projectId, status });
    setProjects((current) => current.map((project) => (project.id === projectId ? toProject(updated) : project)));
  }

  async function updateTodoStatus(todoId: string, status: ProjectTodo["status"]) {
    const previousTodos = todos;
    setTodos((current) => current.map((todo) => (todo.id === todoId ? { ...todo, status } : todo)));

    try {
      const updated = await commandControlClient.households.updateTodoStatus.mutate({ status, todoId });
      setTodos((current) => current.map((todo) => (todo.id === todoId ? updated : todo)));
    } catch {
      setTodos(previousTodos);
    }
  }

  function openTodoModal(projectId: string) {
    setEditingTodoId("");
    setTodoProjectId(projectId);
    setNewTodoTitle("");
    setNewTodoDueDate("");
    setNewTodoStatus("Not Started");
    setTodoCreateError("");
    setTodoModalOpen(true);
  }

  function openTodoEditModal(todo: ProjectTodo) {
    setEditingTodoId(todo.id);
    setTodoProjectId(todo.projectId);
    setNewTodoTitle(todo.title);
    setNewTodoDueDate(todo.dueDate ?? "");
    setNewTodoStatus(todo.status);
    setTodoCreateError("");
    setTodoModalOpen(true);
  }

  async function createTodo() {
    const title = newTodoTitle.trim();
    if (!title) {
      setTodoCreateError("Enter a todo title.");
      return;
    }

    if (!todoProjectId) {
      setTodoCreateError("Open a project before creating a todo.");
      return;
    }

    setTodoCreating(true);
    setTodoCreateError("");

    try {
      const todo = await commandControlClient.households.createTodo.mutate({
        ...(newTodoDueDate ? { dueDate: newTodoDueDate } : {}),
        projectId: todoProjectId,
        title,
      });
      setTodos((current) => [...current, todo]);
      setNewTodoTitle("");
      setNewTodoDueDate("");
      setNewTodoStatus("Not Started");
      setTodoProjectId("");
      setEditingTodoId("");
      setTodoModalOpen(false);
    } catch {
      setTodoCreateError("Could not create the todo. Check your connection and try again.");
    } finally {
      setTodoCreating(false);
    }
  }

  async function saveTodo() {
    const title = newTodoTitle.trim();
    if (!title) {
      setTodoCreateError("Enter a todo title.");
      return;
    }

    if (!editingTodoId) {
      await createTodo();
      return;
    }

    setTodoCreating(true);
    setTodoCreateError("");

    try {
      const todo = await commandControlClient.households.updateTodo.mutate({
        dueDate: newTodoDueDate || null,
        status: newTodoStatus,
        title,
        todoId: editingTodoId,
      });
      setTodos((current) => current.map((currentTodo) => (currentTodo.id === todo.id ? todo : currentTodo)));
      setNewTodoTitle("");
      setNewTodoDueDate("");
      setNewTodoStatus("Not Started");
      setTodoProjectId("");
      setEditingTodoId("");
      setTodoModalOpen(false);
    } catch {
      setTodoCreateError("Could not save the todo. Check your connection and try again.");
    } finally {
      setTodoCreating(false);
    }
  }

  function closeSupplyModal() {
    setSupplyModalOpen(false);
    setSupplyError("");
    setEditingSupplyId("");
    setSupplyProjectId("");
    setSupplyItemName("");
    setSupplyQuantity("1");
    setSupplyStatus("Needed");
    setSupplyEstimatedCost("");
    setSupplyActualCost("");
  }

  function openSupplyModal(projectId: string) {
    setEditingSupplyId("");
    setSupplyProjectId(projectId);
    setSupplyItemName("");
    setSupplyQuantity("1");
    setSupplyStatus("Needed");
    setSupplyEstimatedCost("");
    setSupplyActualCost("");
    setSupplyError("");
    setSupplyModalOpen(true);
  }

  function openSupplyEditModal(supply: ProjectSupply) {
    setEditingSupplyId(supply.id);
    setSupplyProjectId(supply.projectId);
    setSupplyItemName(supply.itemName);
    setSupplyQuantity(String(supply.quantity));
    setSupplyStatus(supply.status);
    setSupplyEstimatedCost(supply.estimatedCost === undefined ? "" : String(supply.estimatedCost));
    setSupplyActualCost(supply.actualCost === undefined ? "" : String(supply.actualCost));
    setSupplyError("");
    setSupplyModalOpen(true);
  }

  function parseSupplyCost(value: string): number | undefined {
    const trimmed = value.trim();
    if (!trimmed) {
      return undefined;
    }

    const cost = Number(trimmed);
    return Number.isFinite(cost) && cost >= 0 ? cost : Number.NaN;
  }

  async function saveSupply() {
    const itemName = supplyItemName.trim();
    const quantity = Number(supplyQuantity);
    const estimatedCost = parseSupplyCost(supplyEstimatedCost);
    const actualCost = parseSupplyCost(supplyActualCost);

    if (!itemName) {
      setSupplyError("Enter a supply name.");
      return;
    }

    if (!supplyProjectId) {
      setSupplyError("Open a project before creating a supply.");
      return;
    }

    if (!Number.isInteger(quantity) || quantity < 0) {
      setSupplyError("Enter a whole-number quantity.");
      return;
    }

    if (Number.isNaN(estimatedCost) || Number.isNaN(actualCost)) {
      setSupplyError("Costs must be positive numbers.");
      return;
    }

    setSupplySaving(true);
    setSupplyError("");

    try {
      if (editingSupplyId) {
        const supply = await commandControlClient.households.updateSupply.mutate({
          actualCost: actualCost ?? null,
          estimatedCost: estimatedCost ?? null,
          itemName,
          quantity,
          status: supplyStatus,
          supplyId: editingSupplyId,
        });
        setSupplies((current) => current.map((currentSupply) => (currentSupply.id === supply.id ? supply : currentSupply)));
      } else {
        const supply = await commandControlClient.households.createSupply.mutate({
          ...(actualCost === undefined ? {} : { actualCost }),
          ...(estimatedCost === undefined ? {} : { estimatedCost }),
          itemName,
          projectId: supplyProjectId,
          quantity,
          status: supplyStatus,
        });
        setSupplies((current) => [...current, supply]);
      }
      closeSupplyModal();
    } catch {
      setSupplyError("Could not save the supply. Check your connection and try again.");
    } finally {
      setSupplySaving(false);
    }
  }

  async function deleteSupply() {
    if (!editingSupplyId || !window.confirm("Delete this supply?")) {
      return;
    }

    setSupplySaving(true);
    setSupplyError("");

    try {
      const deleted = await commandControlClient.households.deleteSupply.mutate({ supplyId: editingSupplyId });
      setSupplies((current) => current.filter((supply) => supply.id !== deleted.supplyId));
      closeSupplyModal();
    } catch {
      setSupplyError("Could not delete the supply. Check your connection and try again.");
    } finally {
      setSupplySaving(false);
    }
  }

  function closePermitModal() {
    setPermitModalOpen(false);
    setPermitError("");
    setEditingPermitId("");
    setPermitProjectId("");
    setPermitTitle("");
    setPermitStatus("Needed");
    setPermitSubmissionDueDate("");
    setPermitApprovalDeadline("");
  }

  function openPermitModal(projectId: string) {
    setEditingPermitId("");
    setPermitProjectId(projectId);
    setPermitTitle("");
    setPermitStatus("Needed");
    setPermitSubmissionDueDate("");
    setPermitApprovalDeadline("");
    setPermitError("");
    setPermitModalOpen(true);
  }

  function openPermitEditModal(permit: Permit) {
    setEditingPermitId(permit.id);
    setPermitProjectId(permit.projectId);
    setPermitTitle(permit.title);
    setPermitStatus(permit.status);
    setPermitSubmissionDueDate(permit.submissionDueDate ?? "");
    setPermitApprovalDeadline(permit.approvalDeadline ?? "");
    setPermitError("");
    setPermitModalOpen(true);
  }

  async function savePermit() {
    const title = permitTitle.trim();

    if (!title) {
      setPermitError("Enter a permit title.");
      return;
    }

    if (!permitProjectId) {
      setPermitError("Open a project before creating a permit.");
      return;
    }

    setPermitSaving(true);
    setPermitError("");

    try {
      if (editingPermitId) {
        const permit = await commandControlClient.households.updatePermit.mutate({
          approvalDeadline: permitApprovalDeadline || null,
          permitId: editingPermitId,
          status: permitStatus,
          submissionDueDate: permitSubmissionDueDate || null,
          title,
        });
        setPermits((current) => current.map((currentPermit) => (currentPermit.id === permit.id ? permit : currentPermit)));
      } else {
        const permit = await commandControlClient.households.createPermit.mutate({
          ...(permitApprovalDeadline ? { approvalDeadline: permitApprovalDeadline } : {}),
          projectId: permitProjectId,
          status: permitStatus,
          ...(permitSubmissionDueDate ? { submissionDueDate: permitSubmissionDueDate } : {}),
          title,
        });
        setPermits((current) => [...current, permit]);
      }
      closePermitModal();
    } catch {
      setPermitError("Could not save the permit. Check your connection and try again.");
    } finally {
      setPermitSaving(false);
    }
  }

  async function deletePermit() {
    if (!editingPermitId || !window.confirm("Delete this permit?")) {
      return;
    }

    setPermitSaving(true);
    setPermitError("");

    try {
      const deleted = await commandControlClient.households.deletePermit.mutate({ permitId: editingPermitId });
      setPermits((current) => current.filter((permit) => permit.id !== deleted.permitId));
      closePermitModal();
    } catch {
      setPermitError("Could not delete the permit. Check your connection and try again.");
    } finally {
      setPermitSaving(false);
    }
  }

  async function updatePermitDetails(permitId: string, input: Omit<Permit, "id" | "projectId">) {
    const permit = await commandControlClient.households.updatePermit.mutate({
      approvalDeadline: input.approvalDeadline ?? null,
      permitId,
      status: input.status,
      submissionDueDate: input.submissionDueDate ?? null,
      title: input.title,
    });
    setPermits((current) => current.map((currentPermit) => (currentPermit.id === permit.id ? permit : currentPermit)));
  }

  async function deletePermitById(permitId: string) {
    const deleted = await commandControlClient.households.deletePermit.mutate({ permitId });
    setPermits((current) => current.filter((permit) => permit.id !== deleted.permitId));
  }

  function emptyProfessionalDraft(): Omit<Professional, "id"> {
    return {
      company: "",
      email: "",
      name: "",
      phone: "",
      specialty: "",
    };
  }

  function closeProjectProfessionalModal() {
    setProjectProfessionalModalOpen(false);
    setProjectProfessionalError("");
    setEditingInteractionId("");
    setProjectProfessionalProjectId("");
    setProjectProfessionalMode("existing");
    setSelectedProfessionalId("");
    setProjectProfessionalTitle("");
    setProjectProfessionalStatus("Scheduled");
    setProjectProfessionalType("Consultation");
    setProjectProfessionalStartsAt(`${today}T09:00`);
    setProjectProfessionalDraft(emptyProfessionalDraft());
  }

  function openProjectProfessionalModal(projectId: string) {
    setEditingInteractionId("");
    setProjectProfessionalProjectId(projectId);
    setProjectProfessionalMode(professionals.length > 0 ? "existing" : "new");
    setSelectedProfessionalId(professionals[0]?.id ?? "");
    setProjectProfessionalTitle("");
    setProjectProfessionalStatus("Scheduled");
    setProjectProfessionalType("Consultation");
    setProjectProfessionalStartsAt(`${today}T09:00`);
    setProjectProfessionalDraft(emptyProfessionalDraft());
    setProjectProfessionalError("");
    setProjectProfessionalModalOpen(true);
  }

  function openProjectProfessionalEditModal(interaction: ProfessionalInteraction) {
    setEditingInteractionId(interaction.id);
    setProjectProfessionalProjectId(interaction.projectId);
    setProjectProfessionalMode("existing");
    setSelectedProfessionalId(interaction.professionalId ?? professionals[0]?.id ?? "");
    setProjectProfessionalTitle(interaction.title);
    setProjectProfessionalStatus(interaction.status);
    setProjectProfessionalType(interaction.type);
    setProjectProfessionalStartsAt(interaction.startsAt.slice(0, 16));
    setProjectProfessionalDraft(emptyProfessionalDraft());
    setProjectProfessionalError("");
    setProjectProfessionalModalOpen(true);
  }

  function projectProfessionalPayload() {
    if (projectProfessionalMode === "existing") {
      return selectedProfessionalId ? { professionalId: selectedProfessionalId } : undefined;
    }

    const name = projectProfessionalDraft.name.trim();
    if (!name) {
      return undefined;
    }

    return {
      professional: {
        company: projectProfessionalDraft.company.trim(),
        email: projectProfessionalDraft.email.trim(),
        name,
        phone: projectProfessionalDraft.phone.trim(),
        specialty: projectProfessionalDraft.specialty.trim(),
      },
    };
  }

  async function saveProjectProfessional() {
    const title = projectProfessionalTitle.trim();
    const professionalPayload = projectProfessionalPayload();

    if (!title) {
      setProjectProfessionalError("Enter a title for this project pro.");
      return;
    }

    if (!projectProfessionalStartsAt.trim()) {
      setProjectProfessionalError("Enter a date and time.");
      return;
    }

    if (!professionalPayload) {
      setProjectProfessionalError(projectProfessionalMode === "existing" ? "Select an existing professional." : "Enter the new professional's name.");
      return;
    }

    if (!projectProfessionalProjectId) {
      setProjectProfessionalError("Open a project before adding a pro.");
      return;
    }

    setProjectProfessionalSaving(true);
    setProjectProfessionalError("");

    try {
      if (editingInteractionId) {
        const result = await commandControlClient.households.updateProjectProfessional.mutate({
          ...professionalPayload,
          interactionId: editingInteractionId,
          startsAt: projectProfessionalStartsAt,
          status: projectProfessionalStatus,
          title,
          type: projectProfessionalType,
        });
        if (result.professional) {
          setProfessionals((current) => [result.professional!, ...current]);
        }
        setInteractions((current) => current.map((interaction) => (interaction.id === result.interaction.id ? result.interaction : interaction)));
      } else {
        const result = await commandControlClient.households.createProjectProfessional.mutate({
          ...professionalPayload,
          projectId: projectProfessionalProjectId,
          startsAt: projectProfessionalStartsAt,
          status: projectProfessionalStatus,
          title,
          type: projectProfessionalType,
        });
        if (result.professional) {
          setProfessionals((current) => [result.professional!, ...current]);
        }
        setInteractions((current) => [...current, result.interaction]);
      }
      closeProjectProfessionalModal();
    } catch {
      setProjectProfessionalError("Could not save the project pro. Check your connection and try again.");
    } finally {
      setProjectProfessionalSaving(false);
    }
  }

  async function deleteProjectProfessional() {
    if (!editingInteractionId || !window.confirm("Remove this pro from the project?")) {
      return;
    }

    setProjectProfessionalSaving(true);
    setProjectProfessionalError("");

    try {
      const deleted = await commandControlClient.households.deleteProjectProfessional.mutate({ interactionId: editingInteractionId });
      setInteractions((current) => current.filter((interaction) => interaction.id !== deleted.interactionId));
      closeProjectProfessionalModal();
    } catch {
      setProjectProfessionalError("Could not remove the pro. Check your connection and try again.");
    } finally {
      setProjectProfessionalSaving(false);
    }
  }

  function closeAttachmentModal() {
    setAttachmentModalOpen(false);
    setAttachmentError("");
    setEditingAttachmentId("");
    setAttachmentHouseholdId("");
    setAttachmentEntityId("");
    setAttachmentFilename("");
    setAttachmentDetectedFilename("");
    setAttachmentContentType("");
    setAttachmentByteSize("");
    setAttachmentFile(null);
    setAttachmentEditing(false);
    if (attachmentObjectUrl) {
      URL.revokeObjectURL(attachmentObjectUrl);
    }
    setAttachmentObjectUrl("");
    setAttachmentContentLoading(false);
  }

  function openAttachmentModal(project: Project) {
    setEditingAttachmentId("");
    setAttachmentHouseholdId(project.householdId);
    setAttachmentEntityId(project.id);
    setAttachmentFilename("");
    setAttachmentDetectedFilename("");
    setAttachmentContentType("");
    setAttachmentByteSize("");
    setAttachmentFile(null);
    setAttachmentEditing(true);
    if (attachmentObjectUrl) {
      URL.revokeObjectURL(attachmentObjectUrl);
    }
    setAttachmentObjectUrl("");
    setAttachmentContentLoading(false);
    setAttachmentError("");
    setAttachmentModalOpen(true);
  }

  async function loadAttachmentContent(attachment: Attachment) {
    if (attachmentObjectUrl) {
      URL.revokeObjectURL(attachmentObjectUrl);
      setAttachmentObjectUrl("");
    }

    setAttachmentContentLoading(true);
    try {
      const response = await fetch(`/attachments/${encodeURIComponent(attachment.id)}/content`, {
        headers: storedAuthToken() ? { authorization: `Bearer ${storedAuthToken()}` } : {},
      });
      if (!response.ok) {
        return;
      }
      setAttachmentObjectUrl(URL.createObjectURL(await response.blob()));
    } finally {
      setAttachmentContentLoading(false);
    }
  }

  function openAttachmentViewModal(attachment: Attachment) {
    setEditingAttachmentId(attachment.id);
    setAttachmentHouseholdId(attachment.householdId);
    setAttachmentEntityId(attachment.entityId);
    setAttachmentFilename(attachment.filename);
    setAttachmentDetectedFilename(attachment.filename);
    setAttachmentContentType(attachment.contentType ?? "");
    setAttachmentByteSize(attachment.byteSize === undefined ? "" : String(attachment.byteSize));
    setAttachmentFile(null);
    setAttachmentEditing(false);
    setAttachmentError("");
    setAttachmentModalOpen(true);
    void loadAttachmentContent(attachment);
  }

  function parseAttachmentByteSize(): number | undefined {
    const trimmed = attachmentByteSize.trim();
    if (!trimmed) {
      return undefined;
    }

    const byteSize = Number(trimmed);
    return Number.isInteger(byteSize) && byteSize >= 0 ? byteSize : Number.NaN;
  }

  async function detectAttachmentMetadata(file: File | undefined) {
    if (!file) {
      return;
    }

    setAttachmentFile(file);
    setAttachmentFilename(file.name);
    setAttachmentDetectedFilename(file.name);
    setAttachmentContentType(await detectFileMimeType(file));
    setAttachmentByteSize(String(file.size));
    setAttachmentError("");
  }

  async function uploadAttachmentContent(attachmentId: string, body: BodyInit, contentType: string) {
    const response = await fetch(`/attachments/${encodeURIComponent(attachmentId)}/content`, {
      body,
      headers: {
        ...(contentType ? { "content-type": contentType } : {}),
        ...(storedAuthToken() ? { authorization: `Bearer ${storedAuthToken()}` } : {}),
      },
      method: "PUT",
    });

    if (!response.ok) {
      throw new Error("Attachment upload failed");
    }
  }

  async function createEntityAttachment(input: {
    body: BodyInit;
    byteSize: number;
    contentType: string;
    entityId: string;
    entityType: AttachmentEntityType;
    filename: string;
    householdId: string;
  }) {
    const attachment = await commandControlClient.attachments.createRecord.mutate({
      byteSize: input.byteSize,
      contentType: input.contentType,
      entityId: input.entityId,
      entityType: input.entityType,
      filename: input.filename,
      householdId: input.householdId,
    });
    await uploadAttachmentContent(attachment.id, input.body, input.contentType);
    setAttachments((current) => [...current, attachment]);
  }

  async function createPermitDocumentAttachment(permit: Permit, project: Project, file: File) {
    const contentType = await detectFileMimeType(file);
    await createEntityAttachment({
      body: file,
      byteSize: file.size,
      contentType,
      entityId: permit.id,
      entityType: "permit",
      filename: file.name,
      householdId: project.householdId,
    });
  }

  async function createPermitMarkdownAttachment(permit: Permit, project: Project, title: string, markdown: string) {
    const filenameBase = title
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 48) || "permit-note";
    const body = new Blob([markdown], { type: "text/markdown" });
    await createEntityAttachment({
      body,
      byteSize: body.size,
      contentType: "text/markdown",
      entityId: permit.id,
      entityType: "permit",
      filename: `${filenameBase}.md`,
      householdId: project.householdId,
    });
  }

  async function saveAttachment() {
    const filename = attachmentFilename.trim() || attachmentDetectedFilename.trim() || "attachment";
    const contentType = attachmentContentType.trim();
    const byteSize = parseAttachmentByteSize();

    if (!editingAttachmentId && attachmentByteSize.trim() === "") {
      setAttachmentError("Choose a file so the size can be detected automatically.");
      return;
    }

    if (Number.isNaN(byteSize)) {
      setAttachmentError("Byte size must be a whole number.");
      return;
    }

    if (!editingAttachmentId && (!attachmentHouseholdId || !attachmentEntityId)) {
      setAttachmentError("Open a project before adding an attachment.");
      return;
    }

    setAttachmentSaving(true);
    setAttachmentError("");

    try {
      if (editingAttachmentId) {
        const attachment = await commandControlClient.attachments.updateRecord.mutate({
          attachmentId: editingAttachmentId,
          byteSize: byteSize ?? null,
          contentType: contentType || null,
          filename,
        });
        if (attachmentFile) {
          await uploadAttachmentContent(attachment.id, attachmentFile, contentType);
        }
        setAttachments((current) => current.map((currentAttachment) => (currentAttachment.id === attachment.id ? attachment : currentAttachment)));
      } else {
        const attachment = await commandControlClient.attachments.createRecord.mutate({
          ...(byteSize === undefined ? {} : { byteSize }),
          ...(contentType ? { contentType } : {}),
          entityId: attachmentEntityId,
          entityType: "project",
          filename,
          householdId: attachmentHouseholdId,
        });
        if (attachmentFile) {
          await uploadAttachmentContent(attachment.id, attachmentFile, contentType);
        }
        setAttachments((current) => [...current, attachment]);
      }
      closeAttachmentModal();
    } catch {
      setAttachmentError("Could not save the attachment. Check your connection and try again.");
    } finally {
      setAttachmentSaving(false);
    }
  }

  async function deleteAttachment() {
    if (!editingAttachmentId || !window.confirm("Delete this attachment?")) {
      return;
    }

    setAttachmentSaving(true);
    setAttachmentError("");

    try {
      const deleted = await commandControlClient.attachments.deleteRecord.mutate({ attachmentId: editingAttachmentId });
      setAttachments((current) => current.filter((attachment) => attachment.id !== deleted.attachmentId));
      closeAttachmentModal();
    } catch {
      setAttachmentError("Could not delete the attachment. Check your connection and try again.");
    } finally {
      setAttachmentSaving(false);
    }
  }

  function downloadAttachment() {
    if (!attachmentObjectUrl) {
      return;
    }

    const link = document.createElement("a");
    link.href = attachmentObjectUrl;
    link.download = attachmentFilename || "attachment";
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  async function createMaintenance() {
    const title = newMaintenanceTitle.trim();
    if (!title || !selectedHousehold) {
      return;
    }

    const item = await commandControlClient.households.createMaintenance.mutate({
      frequency: newMaintenanceFrequency,
      householdId: selectedHousehold.id,
      nextDueDate: newMaintenanceDueDate,
      title,
    });
    setMaintenance((current) => [item, ...current]);
    setNewMaintenanceTitle("");
    setNewMaintenanceFrequency("Monthly");
    setNewMaintenanceDueDate(today);
    setMaintenanceModalOpen(false);
  }

  async function createProfessional() {
    const name = newProfessional.name.trim();
    if (!name) {
      return;
    }

    const professional = await commandControlClient.households.createProfessional.mutate({
      company: newProfessional.company.trim(),
      email: newProfessional.email.trim(),
      name,
      phone: newProfessional.phone.trim(),
      specialty: newProfessional.specialty.trim(),
    });
    setProfessionals((current) => [professional, ...current]);
    setNewProfessional({ company: "", email: "", name: "", phone: "", specialty: "" });
    setProfessionalModalOpen(false);
    navigate(`/pros/${professional.id}`);
  }

  function updateProfessional(professionalId: string, updates: Partial<Professional>) {
    setProfessionals((current) =>
      current.map((professional) => (professional.id === professionalId ? { ...professional, ...updates } : professional)),
    );
    void commandControlClient.households.updateProfessional.mutate({ professionalId, ...updates });
  }

  if (location.pathname === "/auth/callback") {
    return <AuthCallback completeAuthCallback={completeAuthCallback} error={authError} />;
  }

  if (authLoading) {
    return <LoadingScreen />;
  }

  if (!authSession) {
    return (
      <AuthGate
        authError={authError}
        isSubmitting={signInSubmitting}
        signInMessage={signInMessage}
        submitSignIn={submitSignIn}
      />
    );
  }

  return (
    <div className="app-shell" data-sidebar-state={sidebarOpen ? "expanded" : "collapsed"} data-status-rail-state={statusRailOpen ? "expanded" : "collapsed"}>
      <aside
        className="sidebar"
        aria-label="Household navigation"
        data-collapsible="offcanvas"
        data-state={sidebarOpen ? "expanded" : "collapsed"}
      >
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <div className="sidebar-label">
              <div className="eyebrow">Command</div>
              <strong>Center</strong>
            </div>
          </div>
        </div>

        <div className="sidebar-group household-row">
          <div className="household-picker">
            <button
              aria-expanded={householdPickerOpen}
              className="select-field picker-trigger"
              onClick={() => setHouseholdPickerOpen((open) => !open)}
              type="button"
            >
              <HomeIcon size={15} />
              <span className="sidebar-label">{selectedHousehold?.name ?? "Create household"}</span>
              <ChevronDownIcon size={14} />
            </button>

            {householdPickerOpen ? (
              <div className="picker-menu">
                <label className="picker-search">
                  <SearchIcon size={15} />
                  <input
                    autoFocus
                    onChange={(event) => setHouseholdSearch(event.target.value)}
                    placeholder="Search households"
                    value={householdSearch}
                  />
                </label>

                <div className="picker-options">
                  {visibleHouseholds.length === 0 ? (
                    <div className="picker-empty">No households found.</div>
                  ) : null}
                  {visibleHouseholds.map((household) => (
                    <button
                      className={household.id === selectedHouseholdId ? "is-selected" : ""}
                      key={household.id}
                      onClick={() => {
                        setSelectedHouseholdId(household.id);
                        setHouseholdPickerOpen(false);
                        setHouseholdSearch("");
                      }}
                      type="button"
                    >
                      <strong>{household.name}</strong>
                      <span>{household.projectSummary}</span>
                    </button>
                  ))}
                </div>
                <button className="picker-action-button" onClick={openHouseholdModal} type="button">
                  <PlusIcon size={15} />
                  New household
                </button>
              </div>
            ) : null}
          </div>

        </div>

        <nav className="sidebar-group nav-list" aria-label="Primary">
          {navItems.map(({ icon: Icon, id, label, path }) =>
            id === "projects" ? (
              <div className="nav-project-group" key={id}>
                <div className="nav-project-trigger">
                  <NavLink
                    aria-label={label}
                    className={({ isActive }) => (isActive ? "is-selected" : "")}
                    to={path}
                    title={label}
                  >
                    <Icon size={16} />
                    <span className="sidebar-label">{label}</span>
                  </NavLink>
                  <button
                    aria-expanded={projectMenuOpen}
                    aria-label="Toggle recent projects"
                    onClick={() => setProjectMenuOpen((open) => !open)}
                    title="Toggle recent projects"
                    type="button"
                  >
                    <ChevronDownIcon size={14} />
                  </button>
                </div>

                {projectMenuOpen ? (
                  <div className="sidebar-project-list">
                    {recentProjects.map((project) => (
                      <NavLink
                        className={({ isActive }) => (isActive ? "is-selected" : "")}
                        key={project.id}
                        to={`/projects/${project.id}`}
                      >
                        <span className="project-status-dot" data-status={project.status} />
                        <span>{project.name}</span>
                        <small>{areaName(areaSystems, project.areaSystemId)}</small>
                      </NavLink>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : (
              <NavLink
                aria-label={label}
                className={({ isActive }) => (isActive || (id === "dashboard" && location.pathname === "/") ? "is-selected" : "")}
                end={id === "dashboard" || id === "schedule"}
                key={id}
                to={path}
                title={label}
              >
                <Icon size={16} />
                <span className="sidebar-label">{label}</span>
              </NavLink>
            ),
          )}
        </nav>

        <button
          aria-label="Toggle sidebar"
          className="sidebar-rail"
          onClick={() => setSidebarOpen((open) => !open)}
          title="Toggle sidebar"
          type="button"
        />

        <button className="profile-card" onClick={() => void signOut()} title="Sign out" type="button">
          <span className="profile-avatar" aria-hidden="true">{userInitial(authSession.user)}</span>
          <span className="profile-copy">
            <strong>{displayUserName(authSession.user)}</strong>
            <small>{truncateEmail(authSession.user.email)}</small>
          </span>
          <ChevronDownIcon aria-hidden="true" className="profile-chevron" size={18} />
        </button>
      </aside>

      {sidebarOpen ? (
        <button
          aria-label="Close sidebar"
          className="sidebar-scrim"
          onClick={() => setSidebarOpen(false)}
          type="button"
        />
      ) : null}

      <main className="workspace">
        <header className="topbar">
          <div className="topbar-title">
            <button
              aria-label="Toggle sidebar"
              className="icon-button sidebar-inset-trigger"
              onClick={() => setSidebarOpen((open) => !open)}
              title="Toggle sidebar"
              type="button"
            >
              <PanelLeftIcon size={16} />
            </button>
            <div>
              <span>{selectedHousehold?.name}</span>
              <small>{selectedHousehold?.projectSummary}</small>
            </div>
          </div>
          <button
            aria-label="Toggle household sidebar"
            className="icon-button status-rail-trigger"
            onClick={() => setStatusRailOpen((open) => !open)}
            title="Toggle household sidebar"
            type="button"
          >
            <PanelRightIcon size={16} />
          </button>
        </header>

        <section className="command-surface" aria-label="Household command surface">
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={
            <>
              <div className="surface-header">
                <div>
                  <div className="eyebrow">Today</div>
                  <h1>Dashboard</h1>
                </div>
              </div>

              <div className="dashboard-grid">
                <section className="panel attention-panel">
                  <div className="panel-heading">
                    <h2>Priority</h2>
                    <span>Overdue and today</span>
                  </div>
                  <div className="item-list">
                    {attentionItems.map((item) => (
                      <button className="work-item" key={item.id} type="button">
                        <span className={`type-dot ${item.type}`} />
                        <strong>{item.title}</strong>
                        <small>{typeLabel(item.type)} · {formatDate(item.date)}</small>
                      </button>
                    ))}
                  </div>
                </section>

                <section className="panel maintenance-panel">
                  <div className="panel-heading maintenance-heading">
                    <div>
                      <h2>Maintenance</h2>
                      <span>Recurring care</span>
                    </div>
                    <button className="icon-button" onClick={() => setMaintenanceModalOpen(true)} title="Add maintenance" type="button">
                      <PlusIcon size={16} />
                    </button>
                  </div>
                  <div className="item-list">
                    {maintenance.slice(0, 4).map((item) => (
                      <div className="work-item static" key={item.id}>
                        <span className="type-dot maintenance" />
                        <strong>{item.title}</strong>
                        <small>{item.frequency} · {item.nextDueDate ? formatDate(item.nextDueDate) : "No due date"}</small>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="panel active-projects-panel">
                  <div className="panel-heading">
                    <h2>Active Projects</h2>
                  </div>
                  <div className="project-stack">
                    {activeProjects.map((project) => (
                      <Link className="project-card" key={project.id} to={`/projects/${project.id}`}>
                        <strong>{project.name}</strong>
                        <span>{areaName(areaSystems, project.areaSystemId)}</span>
                      </Link>
                    ))}
                  </div>
                </section>

                <section className="panel schedule-panel">
                  <div className="panel-heading">
                    <h2>This Week</h2>
                    <span>Unified schedule</span>
                  </div>
                  <ol className="timeline">
                    {weekItems.map((item) => (
                      <li key={item.id}>
                        <span>{formatDate(item.date)}</span>
                        <strong>{item.title}</strong>
                        <small>{typeLabel(item.type)}</small>
                      </li>
                    ))}
                  </ol>
                </section>

              </div>
            </>
            } />

            <Route path="/projects" element={
              <ProjectListPage
                areaSystems={selectedHouseholdAreaSystems}
                areaFilter={areaFilter}
                filteredProjects={filteredProjects}
                openProjectModal={openProjectModal}
                statusFilter={statusFilter}
                setAreaFilter={setAreaFilter}
                setStatusFilter={setStatusFilter}
              />
            } />

            <Route path="/projects/:projectId" element={
              <ProjectDetailPage
                areaSystems={areaSystems}
                attachments={attachments}
                detailTab={detailTab}
                interactions={interactions}
                invoices={invoices}
                openAttachmentViewModal={openAttachmentViewModal}
                openAttachmentModal={openAttachmentModal}
                openProjectProfessionalEditModal={openProjectProfessionalEditModal}
                openProjectProfessionalModal={openProjectProfessionalModal}
                openPermitModal={openPermitModal}
                openSupplyEditModal={openSupplyEditModal}
                openSupplyModal={openSupplyModal}
                openTodoEditModal={openTodoEditModal}
                openTodoModal={openTodoModal}
                permits={permits}
                professionals={professionals}
                projects={projects}
                quotes={quotes}
                setDetailTab={setDetailTab}
                supplies={supplies}
                todos={todos}
                updateProjectLocation={updateProjectLocation}
                updateProjectName={updateProjectName}
                updateProjectStatus={updateProjectStatus}
                updateTodoStatus={updateTodoStatus}
              />
            } />

            <Route path="/projects/:projectId/permits/:permitId" element={
              <PermitDetailPage
                attachments={attachments}
                createPermitDocumentAttachment={createPermitDocumentAttachment}
                createPermitMarkdownAttachment={createPermitMarkdownAttachment}
                deletePermitById={deletePermitById}
                openAttachmentViewModal={openAttachmentViewModal}
                permits={permits}
                projects={projects}
                updatePermitDetails={updatePermitDetails}
              />
            } />

            <Route path="/maintenance" element={
            <section className="panel full-panel">
              <div className="panel-heading maintenance-heading"><div><h2>Maintenance</h2><span>One-time and recurring household care</span></div><button className="icon-button" onClick={() => setMaintenanceModalOpen(true)} title="Add maintenance" type="button"><PlusIcon size={16} /></button></div>
              <div className="maintenance-grid">{maintenance.map((item) => <Link className="work-item" key={item.id} to={`/maintenance/${item.id}`}><span className="type-dot maintenance" /><strong>{item.title}</strong><small>{item.status} · {item.frequency} · {item.nextDueDate ? formatDate(item.nextDueDate) : "No due date"}</small></Link>)}</div>
            </section>
            } />

            <Route path="/maintenance/:maintenanceId" element={<MaintenanceDetailPage maintenance={maintenance} />} />

            <Route path="/schedule" element={
            <section className="panel full-panel">
              <div className="surface-header compact"><div><div className="eyebrow">Unified Dates</div><h1>Schedule</h1></div><div className="segmented-control">{(["agenda", "month", "week", "day"] as const).map((mode) => <button className={scheduleMode === mode ? "is-active" : ""} key={mode} onClick={() => setScheduleMode(mode)} type="button">{titleCase(mode)}</button>)}</div></div>

              {scheduleMode === "agenda" ? (
                <div className="agenda-list">{weekItems.map((item) => <div className="calendar-item" key={item.id}><span>{formatDate(item.date)}</span><strong>{item.title}</strong><small>{typeLabel(item.type)}</small></div>)}</div>
              ) : null}

              {scheduleMode === "month" ? (
                <div className="calendar-shell">
                  <div className="calendar-title"><strong>{monthLabel}</strong><span>{scheduleItems.length} scheduled</span></div>
                  <div className="month-calendar">
                    {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => <div className="calendar-weekday" key={day}>{day}</div>)}
                    {monthDays.map((day) => {
                      const dayItemsForCell = itemsForDate(scheduleItems, day);
                      return (
                        <div className={day.getMonth() === scheduleAnchor.getMonth() ? "month-cell" : "month-cell is-muted"} key={toDateKey(day)}>
                          <span className="day-number">{day.getDate()}</span>
                          <div className="calendar-event-stack">
                            {dayItemsForCell.slice(0, 3).map((item) => <div className={`calendar-chip ${item.type}`} key={item.id}>{item.title}</div>)}
                            {dayItemsForCell.length > 3 ? <small>+{dayItemsForCell.length - 3} more</small> : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {scheduleMode === "week" ? (
                <div className="calendar-shell">
                  <div className="calendar-title"><strong>{formatDate(weekStartKey)} - {formatDate(weekEndKey)}</strong><span>{weekItems.length} scheduled</span></div>
                  <div className="week-calendar">
                    {weekDays.map((day) => (
                      <div className="week-column" key={toDateKey(day)}>
                        <div className="week-column-heading"><span>{new Intl.DateTimeFormat("en-US", { weekday: "short" }).format(day)}</span><strong>{day.getDate()}</strong></div>
                        <div className="calendar-event-stack">
                          {itemsForDate(weekItems, day).map((item) => <div className={`calendar-chip ${item.type}`} key={item.id}><span>{typeLabel(item.type)}</span>{item.title}</div>)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {scheduleMode === "day" ? (
                <div className="calendar-shell">
                  <div className="calendar-title"><strong>{dayLabel}</strong><span>{dayItems.length} scheduled</span></div>
                  <div className="day-calendar">
                    <div className="all-day-row">
                      <span>All day</span>
                      <div className="calendar-event-stack">{allDayItems.map((item) => <div className={`calendar-chip ${item.type}`} key={item.id}>{item.title}</div>)}</div>
                    </div>
                    {dayHours.map((hour) => (
                      <div className="hour-row" key={hour}>
                        <span>{new Intl.DateTimeFormat("en-US", { hour: "numeric" }).format(new Date(2026, 0, 1, hour))}</span>
                        <div className="calendar-event-stack">
                          {dayItems.filter((item) => itemHour(item) === hour).map((item) => <div className={`calendar-chip ${item.type}`} key={item.id}><span>{typeLabel(item.type)}</span>{item.title}</div>)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </section>
            } />

            <Route path="/pros" element={
            <section className="panel full-panel">
              <div className="panel-heading maintenance-heading"><div><h2>Professionals</h2><span>{professionals.length} saved</span></div><button className="icon-button" onClick={() => setProfessionalModalOpen(true)} title="Add professional" type="button"><PlusIcon size={16} /></button></div>
              <div className="professional-grid">{professionals.map((pro) => <Link aria-label={`Open ${pro.name}`} className="professional-row" key={pro.id} title={pro.name} to={`/pros/${pro.id}`}><UsersIcon size={16} /><strong>{pro.name}</strong><span>{pro.company}</span><small>{pro.specialty} · {pro.phone} · {pro.email}</small></Link>)}</div>
            </section>
            } />
            <Route path="/pros/:professionalId" element={<ProfessionalDetailPage professionals={professionals} updateProfessional={updateProfessional} />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </section>
      </main>

      <aside className="status-rail" aria-label="Status" data-state={statusRailOpen ? "expanded" : "collapsed"}>
        <div className="rail-header">
          <FileTextIcon size={17} />
          <strong>Household</strong>
        </div>
        <section className="rail-section">
          <h2>Records</h2>
          <div className="environment-card"><HammerIcon size={15} /><div><strong>{householdProjects.length} projects</strong><span>Complete hidden by default</span></div></div>
          <div className="environment-card"><ClipboardListIcon size={15} /><div><strong>{todos.length} todos</strong><span>Due dates feed schedule</span></div></div>
          <div className="environment-card"><DollarSignIcon size={15} /><div><strong>{formatMoney(statusRailCosts.actualCost)}</strong><span>Active actual cost</span></div></div>
        </section>
        <section className="rail-section">
          <h2>Storage</h2>
          <ol className="timeline compact-list">
            <li><CommandIcon size={14} /><span>Worker API {backendStatus}</span></li>
            <li><PaperclipIcon size={14} /><span>Private R2 keys by household/entity</span></li>
            <li><MoreHorizontalIcon size={14} /><span>Soft deletion and audit metadata in domain</span></li>
          </ol>
        </section>
      </aside>

      {maintenanceModalOpen ? (
        <div className="modal-backdrop" role="presentation">
          <form
            aria-labelledby="maintenance-modal-title"
            className="modal-panel"
            onSubmit={(event) => {
              event.preventDefault();
              createMaintenance();
            }}
          >
            <div className="modal-heading">
              <div>
                <div className="eyebrow">Maintenance</div>
                <h2 id="maintenance-modal-title">Add Task</h2>
              </div>
              <button
                aria-label="Close"
                className="icon-button"
                onClick={() => setMaintenanceModalOpen(false)}
                title="Close"
                type="button"
              >
                <XIcon size={16} />
              </button>
            </div>

            <label className="modal-field">
              <span>Title</span>
              <input
                autoFocus
                onChange={(event) => setNewMaintenanceTitle(event.target.value)}
                placeholder="Annual HVAC service"
                value={newMaintenanceTitle}
              />
            </label>

            <label className="modal-field">
              <span>Frequency</span>
              <select
                onChange={(event) => setNewMaintenanceFrequency(event.target.value as MaintenanceFrequency)}
                value={newMaintenanceFrequency}
              >
                {["One-time", "Monthly", "Quarterly", "Yearly", "Custom"].map((frequency) => (
                  <option key={frequency} value={frequency}>{frequency}</option>
                ))}
              </select>
            </label>

            <label className="modal-field">
              <span>Next Due</span>
              <input
                onChange={(event) => setNewMaintenanceDueDate(event.target.value)}
                type="date"
                value={newMaintenanceDueDate}
              />
            </label>

            <div className="modal-actions">
              <button className="icon-text-button" onClick={() => setMaintenanceModalOpen(false)} type="button">Cancel</button>
              <button className="icon-text-button primary-action" type="submit">Add</button>
            </div>
          </form>
        </div>
      ) : null}

      {professionalModalOpen ? (
        <div className="modal-backdrop" role="presentation">
          <form
            aria-labelledby="professional-modal-title"
            className="modal-panel"
            onSubmit={(event) => {
              event.preventDefault();
              createProfessional();
            }}
          >
            <div className="modal-heading">
              <div>
                <div className="eyebrow">Professional</div>
                <h2 id="professional-modal-title">Add</h2>
              </div>
              <button
                aria-label="Close"
                className="icon-button"
                onClick={() => setProfessionalModalOpen(false)}
                title="Close"
                type="button"
              >
                <XIcon size={16} />
              </button>
            </div>

            {(["name", "company", "specialty", "phone", "email"] as const).map((field) => (
              <label className="modal-field" key={field}>
                <span>{titleCase(field)}</span>
                <input
                  autoFocus={field === "name"}
                  onChange={(event) => setNewProfessional((current) => ({ ...current, [field]: event.target.value }))}
                  value={newProfessional[field]}
                />
              </label>
            ))}

            <div className="modal-actions">
              <button className="icon-text-button" onClick={() => setProfessionalModalOpen(false)} type="button">Cancel</button>
              <button className="icon-text-button primary-action" type="submit">Add</button>
            </div>
          </form>
        </div>
      ) : null}

      {householdModalOpen ? (
        <div className="modal-backdrop" role="presentation">
          <form
            aria-labelledby="household-modal-title"
            className="modal-panel"
            onSubmit={(event) => {
              event.preventDefault();
              void createHousehold();
            }}
          >
            <div className="modal-heading">
              <div>
                <div className="eyebrow">Household</div>
                <h2 id="household-modal-title">Create household</h2>
              </div>
              <button
                aria-label="Close"
                className="icon-button"
                onClick={() => {
                  setHouseholdModalOpen(false);
                  setHouseholdCreateError("");
                }}
                title="Close"
                type="button"
              >
                <XIcon size={16} />
              </button>
            </div>

            <label className="modal-field">
              <span>Name</span>
              <input
                autoFocus
                onChange={(event) => setNewHouseholdName(event.target.value)}
                placeholder="Maple House"
                value={newHouseholdName}
              />
            </label>

            <label className="modal-field">
              <span>Summary</span>
              <input
                onChange={(event) => setNewHouseholdSummary(event.target.value)}
                placeholder="Renovation-heavy family home"
                value={newHouseholdSummary}
              />
            </label>

            {householdCreateError ? <p className="auth-error">{householdCreateError}</p> : null}

            <div className="modal-actions">
              <button
                className="icon-text-button"
                onClick={() => {
                  setHouseholdModalOpen(false);
                  setHouseholdCreateError("");
                }}
                type="button"
              >
                Cancel
              </button>
              <button className="icon-text-button primary-action" disabled={householdCreating} type="submit">
                {householdCreating ? <LoaderCircleIcon className="spin-icon" size={16} /> : null}
                Create household
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {attachmentModalOpen ? (
        <div className="modal-backdrop" role="presentation">
          <form
            aria-labelledby="attachment-modal-title"
            className="modal-panel"
            onSubmit={(event) => {
              event.preventDefault();
              void saveAttachment();
            }}
          >
            <div className="modal-heading">
              <div>
                <div className="eyebrow">Attachment</div>
                <h2 id="attachment-modal-title">{editingAttachmentId && !attachmentEditing ? attachmentFilename : editingAttachmentId ? "Edit attachment" : "Add attachment"}</h2>
              </div>
              <button
                aria-label="Close"
                className="icon-button"
                onClick={closeAttachmentModal}
                title="Close"
                type="button"
              >
                <XIcon size={16} />
              </button>
            </div>

            {editingAttachmentId && !attachmentEditing ? (
              <div className="attachment-viewer">
                {attachmentContentLoading ? <div className="attachment-preview-empty">Loading</div> : null}
                {!attachmentContentLoading && attachmentObjectUrl && isViewableAttachment(attachmentContentType) ? (
                  attachmentContentType === "application/pdf" ? (
                    <iframe className="attachment-preview-frame" src={attachmentObjectUrl} title={attachmentFilename} />
                  ) : (
                    <img alt={attachmentFilename} className="attachment-preview-image" src={attachmentObjectUrl} />
                  )
                ) : null}
                {!attachmentContentLoading && (!attachmentObjectUrl || !isViewableAttachment(attachmentContentType)) ? (
                  <div className="attachment-preview-empty">{attachmentObjectUrl ? "Preview unavailable" : "No content"}</div>
                ) : null}
                <div className="detected-metadata">
                  <div>
                    <span>Content type</span>
                    <strong>{attachmentContentType || "Unknown"}</strong>
                  </div>
                  <div>
                    <span>Size</span>
                    <strong>{formatByteSize(attachmentByteSize ? Number(attachmentByteSize) : undefined)}</strong>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <label className={attachmentDetectedFilename ? "attachment-picker has-file" : "attachment-picker"}>
                  <input
                    autoFocus={!editingAttachmentId}
                    onChange={(event) => {
                      void detectAttachmentMetadata(event.target.files?.[0]);
                    }}
                    type="file"
                  />
                  <span className="attachment-picker-copy">
                    <strong>{attachmentDetectedFilename || "No file"}</strong>
                  </span>
                </label>

                {attachmentDetectedFilename ? (
                  <label className="modal-field">
                    <span>Filename override</span>
                    <input
                      autoFocus={Boolean(editingAttachmentId)}
                      onChange={(event) => setAttachmentFilename(event.target.value)}
                      placeholder={attachmentDetectedFilename}
                      value={attachmentFilename}
                    />
                  </label>
                ) : null}

                <div className="detected-metadata">
                  <div>
                    <span>Content type</span>
                    <strong>{attachmentContentType || "Unknown"}</strong>
                  </div>
                  <div>
                    <span>Size</span>
                    <strong>{formatByteSize(attachmentByteSize ? Number(attachmentByteSize) : undefined)}</strong>
                  </div>
                </div>
              </>
            )}

            {attachmentError ? <p className="auth-error">{attachmentError}</p> : null}

            <div className="modal-actions split-actions">
              {editingAttachmentId && attachmentEditing ? (
                <button className="icon-text-button danger-action" disabled={attachmentSaving} onClick={() => void deleteAttachment()} type="button">
                  Delete
                </button>
              ) : <span />}
              <div className="modal-secondary-actions">
                <button className="icon-text-button" onClick={closeAttachmentModal} type="button">Cancel</button>
                {editingAttachmentId && !attachmentEditing && attachmentObjectUrl ? (
                  <button className="icon-text-button" onClick={downloadAttachment} type="button">Download</button>
                ) : null}
                {editingAttachmentId && !attachmentEditing ? (
                  <button className="icon-text-button primary-action" onClick={() => setAttachmentEditing(true)} type="button">Edit</button>
                ) : (
                  <button className="icon-text-button primary-action" disabled={attachmentSaving} type="submit">
                    {attachmentSaving ? <LoaderCircleIcon className="spin-icon" size={16} /> : null}
                    {editingAttachmentId ? "Save attachment" : "Add attachment"}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      ) : null}

      {projectProfessionalModalOpen ? (
        <div className="modal-backdrop" role="presentation">
          <form
            aria-labelledby="project-professional-modal-title"
            className="modal-panel"
            onSubmit={(event) => {
              event.preventDefault();
              void saveProjectProfessional();
            }}
          >
            <div className="modal-heading">
              <div>
                <div className="eyebrow">Project Pro</div>
                <h2 id="project-professional-modal-title">{editingInteractionId ? "Edit pro" : "Add pro"}</h2>
              </div>
              <button
                aria-label="Close"
                className="icon-button"
                onClick={closeProjectProfessionalModal}
                title="Close"
                type="button"
              >
                <XIcon size={16} />
              </button>
            </div>

            <div className="segmented-control modal-segmented-control" role="tablist" aria-label="Professional source">
              {(["existing", "new"] as const).map((mode) => (
                <button
                  aria-selected={projectProfessionalMode === mode}
                  className={projectProfessionalMode === mode ? "is-active" : ""}
                  key={mode}
                  onClick={() => setProjectProfessionalMode(mode)}
                  role="tab"
                  type="button"
                >
                  {mode === "existing" ? "Existing" : "New"}
                </button>
              ))}
            </div>

            {projectProfessionalMode === "existing" ? (
              <label className="modal-field">
                <span>Professional</span>
                <select
                  autoFocus
                  onChange={(event) => setSelectedProfessionalId(event.target.value)}
                  value={selectedProfessionalId}
                >
                  <option value="">Select a professional</option>
                  {professionals.map((professional) => (
                    <option key={professional.id} value={professional.id}>
                      {professional.name} · {professional.company || professional.specialty || "Saved pro"}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <>
                {(["name", "company", "specialty", "phone", "email"] as const).map((field) => (
                  <label className="modal-field" key={field}>
                    <span>{titleCase(field)}</span>
                    <input
                      autoFocus={field === "name"}
                      onChange={(event) => setProjectProfessionalDraft((current) => ({ ...current, [field]: event.target.value }))}
                      value={projectProfessionalDraft[field]}
                    />
                  </label>
                ))}
              </>
            )}

            <label className="modal-field">
              <span>Title</span>
              <input
                onChange={(event) => setProjectProfessionalTitle(event.target.value)}
                placeholder="Estimate walkthrough"
                value={projectProfessionalTitle}
              />
            </label>

            <label className="modal-field">
              <span>Type</span>
              <select
                onChange={(event) => setProjectProfessionalType(event.target.value as ProfessionalInteraction["type"])}
                value={projectProfessionalType}
              >
                {["Consultation", "Estimate", "Work Visit", "Inspection", "Follow-up", "Walkthrough"].map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </label>

            <label className="modal-field">
              <span>Status</span>
              <select
                onChange={(event) => setProjectProfessionalStatus(event.target.value as ProfessionalInteraction["status"])}
                value={projectProfessionalStatus}
              >
                {["Scheduled", "Completed", "Cancelled"].map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </label>

            <label className="modal-field">
              <span>Date and time</span>
              <input
                onChange={(event) => setProjectProfessionalStartsAt(event.target.value)}
                type="datetime-local"
                value={projectProfessionalStartsAt}
              />
            </label>

            {projectProfessionalError ? <p className="auth-error">{projectProfessionalError}</p> : null}

            <div className="modal-actions split-actions">
              {editingInteractionId ? (
                <button className="icon-text-button danger-action" disabled={projectProfessionalSaving} onClick={() => void deleteProjectProfessional()} type="button">
                  Remove
                </button>
              ) : <span />}
              <div className="modal-secondary-actions">
                <button className="icon-text-button" onClick={closeProjectProfessionalModal} type="button">Cancel</button>
                <button className="icon-text-button primary-action" disabled={projectProfessionalSaving} type="submit">
                  {projectProfessionalSaving ? <LoaderCircleIcon className="spin-icon" size={16} /> : null}
                  {editingInteractionId ? "Save pro" : "Add pro"}
                </button>
              </div>
            </div>
          </form>
        </div>
      ) : null}

      {permitModalOpen ? (
        <div className="modal-backdrop" role="presentation">
          <form
            aria-labelledby="permit-modal-title"
            className="modal-panel"
            onSubmit={(event) => {
              event.preventDefault();
              void savePermit();
            }}
          >
            <div className="modal-heading">
              <div>
                <div className="eyebrow">Permit</div>
                <h2 id="permit-modal-title">{editingPermitId ? "Edit permit" : "Add permit"}</h2>
              </div>
              <button
                aria-label="Close"
                className="icon-button"
                onClick={closePermitModal}
                title="Close"
                type="button"
              >
                <XIcon size={16} />
              </button>
            </div>

            <label className="modal-field">
              <span>Title</span>
              <input
                autoFocus
                onChange={(event) => setPermitTitle(event.target.value)}
                placeholder="Electrical rough-in permit"
                value={permitTitle}
              />
            </label>

            <label className="modal-field">
              <span>Status</span>
              <select
                onChange={(event) => setPermitStatus(event.target.value as Permit["status"])}
                value={permitStatus}
              >
                {["Needed", "Drafting", "Submitted", "Approved", "Rejected", "Closed"].map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </label>

            <label className="modal-field">
              <span>Submission due date</span>
              <input
                onChange={(event) => setPermitSubmissionDueDate(event.target.value)}
                type="date"
                value={permitSubmissionDueDate}
              />
            </label>

            <label className="modal-field">
              <span>Approval deadline</span>
              <input
                onChange={(event) => setPermitApprovalDeadline(event.target.value)}
                type="date"
                value={permitApprovalDeadline}
              />
            </label>

            {permitError ? <p className="auth-error">{permitError}</p> : null}

            <div className="modal-actions split-actions">
              {editingPermitId ? (
                <button className="icon-text-button danger-action" disabled={permitSaving} onClick={() => void deletePermit()} type="button">
                  Delete
                </button>
              ) : <span />}
              <div className="modal-secondary-actions">
                <button className="icon-text-button" onClick={closePermitModal} type="button">Cancel</button>
                <button className="icon-text-button primary-action" disabled={permitSaving} type="submit">
                  {permitSaving ? <LoaderCircleIcon className="spin-icon" size={16} /> : null}
                  {editingPermitId ? "Save permit" : "Add permit"}
                </button>
              </div>
            </div>
          </form>
        </div>
      ) : null}

      {supplyModalOpen ? (
        <div className="modal-backdrop" role="presentation">
          <form
            aria-labelledby="supply-modal-title"
            className="modal-panel"
            onSubmit={(event) => {
              event.preventDefault();
              void saveSupply();
            }}
          >
            <div className="modal-heading">
              <div>
                <div className="eyebrow">Supply</div>
                <h2 id="supply-modal-title">{editingSupplyId ? "Edit supply" : "Add supply"}</h2>
              </div>
              <button
                aria-label="Close"
                className="icon-button"
                onClick={closeSupplyModal}
                title="Close"
                type="button"
              >
                <XIcon size={16} />
              </button>
            </div>

            <label className="modal-field">
              <span>Item</span>
              <input
                autoFocus
                onChange={(event) => setSupplyItemName(event.target.value)}
                placeholder="Shelf brackets"
                value={supplyItemName}
              />
            </label>

            <label className="modal-field">
              <span>Quantity</span>
              <input
                min="0"
                onChange={(event) => setSupplyQuantity(event.target.value)}
                step="1"
                type="number"
                value={supplyQuantity}
              />
            </label>

            <label className="modal-field">
              <span>Status</span>
              <select
                onChange={(event) => setSupplyStatus(event.target.value as ProjectSupply["status"])}
                value={supplyStatus}
              >
                {["Needed", "Ordered", "Purchased", "Delivered", "Installed"].map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </label>

            <label className="modal-field">
              <span>Estimated cost</span>
              <input
                min="0"
                onChange={(event) => setSupplyEstimatedCost(event.target.value)}
                placeholder="0.00"
                step="0.01"
                type="number"
                value={supplyEstimatedCost}
              />
            </label>

            <label className="modal-field">
              <span>Actual cost</span>
              <input
                min="0"
                onChange={(event) => setSupplyActualCost(event.target.value)}
                placeholder="0.00"
                step="0.01"
                type="number"
                value={supplyActualCost}
              />
            </label>

            {supplyError ? <p className="auth-error">{supplyError}</p> : null}

            <div className="modal-actions split-actions">
              {editingSupplyId ? (
                <button className="icon-text-button danger-action" disabled={supplySaving} onClick={() => void deleteSupply()} type="button">
                  Delete
                </button>
              ) : <span />}
              <div className="modal-secondary-actions">
                <button className="icon-text-button" onClick={closeSupplyModal} type="button">Cancel</button>
                <button className="icon-text-button primary-action" disabled={supplySaving} type="submit">
                  {supplySaving ? <LoaderCircleIcon className="spin-icon" size={16} /> : null}
                  {editingSupplyId ? "Save supply" : "Add supply"}
                </button>
              </div>
            </div>
          </form>
        </div>
      ) : null}

      {todoModalOpen ? (
        <div className="modal-backdrop" role="presentation">
          <form
            aria-labelledby="todo-modal-title"
            className="modal-panel"
            onSubmit={(event) => {
              event.preventDefault();
              void saveTodo();
            }}
          >
            <div className="modal-heading">
              <div>
                <div className="eyebrow">Todo</div>
                <h2 id="todo-modal-title">{editingTodoId ? "Edit todo" : "Add todo"}</h2>
              </div>
              <button
                aria-label="Close"
                className="icon-button"
                onClick={() => {
                  setTodoModalOpen(false);
                  setTodoCreateError("");
                  setEditingTodoId("");
                  setNewTodoStatus("Not Started");
                }}
                title="Close"
                type="button"
              >
                <XIcon size={16} />
              </button>
            </div>

            <label className="modal-field">
              <span>Title</span>
              <input
                autoFocus
                onChange={(event) => setNewTodoTitle(event.target.value)}
                placeholder="Measure cabinet run"
                value={newTodoTitle}
              />
            </label>

            <label className="modal-field">
              <span>Due date</span>
              <input
                onChange={(event) => setNewTodoDueDate(event.target.value)}
                type="date"
                value={newTodoDueDate}
              />
            </label>

            <label className="modal-field">
              <span>Status</span>
              <select
                onChange={(event) => setNewTodoStatus(event.target.value as ProjectTodo["status"])}
                value={newTodoStatus}
              >
                {["Not Started", "In Progress", "Blocked", "Done"].map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </label>

            {todoCreateError ? <p className="auth-error">{todoCreateError}</p> : null}

            <div className="modal-actions">
              <button
                className="icon-text-button"
                onClick={() => {
                  setTodoModalOpen(false);
                  setTodoCreateError("");
                  setEditingTodoId("");
                  setNewTodoStatus("Not Started");
                }}
                type="button"
              >
                Cancel
              </button>
              <button className="icon-text-button primary-action" disabled={todoCreating} type="submit">
                {todoCreating ? <LoaderCircleIcon className="spin-icon" size={16} /> : null}
                {editingTodoId ? "Save todo" : "Add todo"}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {projectModalOpen ? (
        <div className="modal-backdrop" role="presentation">
          <form
            aria-labelledby="project-modal-title"
            className="modal-panel"
            onSubmit={(event) => {
              event.preventDefault();
              void createProject();
            }}
          >
            <div className="modal-heading">
              <div>
                <div className="eyebrow">Projects</div>
                <h2 id="project-modal-title">Create project</h2>
              </div>
              <button
                aria-label="Close"
                className="icon-button"
                onClick={() => {
                  setProjectModalOpen(false);
                  setProjectCreateError("");
                  setNewProjectAreaSystemName("");
                }}
                title="Close"
                type="button"
              >
                <XIcon size={16} />
              </button>
            </div>

            <p className="modal-copy">Create a new project for {selectedHousehold?.name ?? "the selected household"}.</p>

            <label className="modal-field">
              <span>Name</span>
              <input
                autoFocus
                onChange={(event) => setNewProjectName(event.target.value)}
                placeholder="New Project 1"
                value={newProjectName}
              />
            </label>

            <label className="modal-field">
              <span>Area</span>
              <input
                list="project-area-options"
                onChange={(event) => setNewProjectAreaSystemName(event.target.value)}
                placeholder="Whole House"
                value={newProjectAreaSystemName}
              />
              <datalist id="project-area-options">
                {selectedHouseholdAreaSystems.map((area) => (
                  <option key={area.id} value={area.name} />
                ))}
              </datalist>
            </label>

            {projectCreateError ? <p className="auth-error">{projectCreateError}</p> : null}

            <div className="modal-actions">
              <button
                className="icon-text-button"
                onClick={() => {
                  setProjectModalOpen(false);
                  setProjectCreateError("");
                  setNewProjectAreaSystemName("");
                }}
                type="button"
              >
                Cancel
              </button>
              <button className="icon-text-button primary-action" disabled={projectCreating} type="submit">
                {projectCreating ? <LoaderCircleIcon className="spin-icon" size={16} /> : null}
                Create project
              </button>
            </div>
          </form>
        </div>
      ) : null}

    </div>
  );
}

export default App;
